/*
 * Состояние приложения.
 *
 * Начальный снимок приходит по REST, а изменения — через WebSocket. Компоненты
 * не держат собственные копии серверных сущностей и вызывают только методы
 * этого store.
 */

import {
	type ActiveCall,
	type Attachment,
	type CallKind,
	type CallRecord,
	type CallSidePanel,
	type CallViewMode,
	type Chat,
	type ChatFolder,
	type ChatRole,
	type FolderRules,
	type MemberPermissions,
	type Message,
	type ServerCall,
	type User
} from '@andresot/protocol';
import { SvelteMap } from 'svelte/reactivity';
import type {
	CreatePollPayload,
	EphemeralMedia,
	ReactMessagePayload,
	ServerMessage
} from '@andresot/protocol/envelope';
import {
	fetchChats,
	fetchMessages,
	clearChatHistory,
	createManagedChat,
	deleteChat,
	editMessage as editChatMessage,
	fetchPinnedMessages,
	fetchUsers,
	updateChatPreferences
} from '$api/chats';
import { uploadFile } from '$api/upload';
import { ApiError, NetworkError } from '$api/client';
import { createDialog, fetchContacts } from '$api/account';
import * as folderApi from '$api/folders';
import type { ChatUpdate } from '@andresot/protocol/chat-management';
import * as chatManagementApi from '$api/chat-management';
import * as callApi from '$api/calls';
import { claimAnnouncements, type ServiceAnnouncement } from '$api/announcements';
import { admin } from './admin.svelte';
import { socket, SocketRequestError, type SocketPhase } from '$api/socket';
import { callMedia, type InitialCallMediaOptions } from '$stores/call-media.svelte';
import { messageSound } from '$stores/message-sound';
import { callRingtone } from '$stores/call-ringtone';
import { toasts } from '$stores/toasts.svelte';
import { maintenance } from './maintenance.svelte';
import { mail } from './mail.svelte';
import type { AccountUser } from '$api/auth';
import { readSnapshot, writeSnapshot } from '$utils/snapshot';
import { readDrafts, writeDrafts } from '$utils/drafts';
import { appOnScreen, watchOnScreen } from '$utils/on-screen';
import { prefs } from '$stores/prefs.svelte';

export type Section = 'chats' | 'calls' | 'contacts' | 'mail' | 'services' | 'settings';

function screenShareError(error: unknown): string {
	const name = error instanceof Error ? error.name : '';
	if (name === 'NotFoundError') return 'Нет доступного экрана для демонстрации';
	if (name === 'NotReadableError') return 'Браузер не смог начать демонстрацию выбранного экрана';
	if (name === 'InvalidStateError') return 'Вернитесь в окно звонка и попробуйте ещё раз';
	return 'Не удалось начать демонстрацию экрана';
}

/*
 * Звонки, которые ведёт именно эта вкладка.
 *
 * Сервер знает человека, а не устройство: «принял» и «в звонке» приходят всем
 * его вкладкам и телефонам сразу. Без своей отметки второе устройство входило
 * в чужой для него звонок само — с включённым микрофоном — и выбивало первое:
 * участник с одним именем в комнате один.
 *
 * Хранилище вкладки, а не общее: перезагрузка звонок не теряет, а соседняя
 * вкладка его не получает.
 */
const CALLS_HERE_KEY = 'andresot:calls-here';

/** Отрезок переписки, известный целиком, — по номерам сообщений. */
interface FeedRange {
	lo: number;
	hi: number;
}

/** Верхняя граница последнего куска: он открыт, в него попадает всё новое. */
const FEED_OPEN_END = Number.MAX_SAFE_INTEGER;
/** Сколько сообщений просим за раз при подгрузке. */
const FEED_PAGE = 100;

/** Сколько отметка «печатает…» держится без подтверждения. */
const TYPING_SHOWN_MS = 4_000;
/** Как часто сообщаем о своём наборе: чаще, чем гаснет отметка у собеседника. */
const TYPING_SEND_EVERY_MS = 3_000;

function readCallsHere(): string[] {
	try {
		const value: unknown = JSON.parse(sessionStorage.getItem(CALLS_HERE_KEY) ?? '[]');
		return Array.isArray(value)
			? value.filter((item): item is string => typeof item === 'string')
			: [];
	} catch {
		return [];
	}
}

function writeCallsHere(ids: Iterable<string>): void {
	try {
		sessionStorage.setItem(CALLS_HERE_KEY, JSON.stringify([...ids]));
	} catch {
		// Хранилище закрыто — отметка проживёт до перезагрузки вкладки.
	}
}

class AppState {
	/* --- Кто мы ------------------------------------------------------- */
	// Не readonly: после подтверждённого API-обновления профиль заменяется
	// свежим серверным представлением без перезагрузки приложения.
	/** Заполняется при входе из session.svelte.ts. */
	me = $state<User>({
		id: '',
		login: '',
		name: '',
		presence: 'online'
	});

	/* --- Навигация ----------------------------------------------------- */
	section = $state<Section>('chats');
	/*
	 * Ничего не выбрано, пока серверный список чатов не загружен. После
	 * загрузки desktop откроет первый доступный чат, а mobile оставит список.
	 */
	selectedChatId = $state<string | null>(null);
	searchQuery = $state('');
	/** Открыта правая панель со сведениями о чате. */
	infoPanelOpen = $state(false);
	/** Звонок заведён, но мы ещё на экране подготовки и не подключились. */
	private preparedCall: ServerCall | null = null;

	/* --- Папки --------------------------------------------------------- */
	folders = $state<ChatFolder[]>([]);
	activeFolderId = $state('');

	/*
	 * Кто у меня в контактах. Держим здесь, а не только в панели контактов:
	 * от этого зависят правила папок «Контакты» и «Не контакты», а они
	 * пересчитываются на каждое изменение списка чатов.
	 */
	contactIds = $state<Set<string>>(new Set());

	/*
	 * Своё имя контакта — то, как человек записан в книжке. Отдельно от
	 * `contactIds`, потому что нужно не для правил, а для показа: собеседника
	 * зовут так, как его назвали вы, а не так, как он назвал себя сам.
	 */
	contactNames = $state<Record<string, string>>({});

	/*
	 * Состояние связи с сервером. Само по себе `socket.phase` — обычное поле,
	 * разметка на него не отзывается; здесь оно становится видимым для неё.
	 * Нужно, чтобы «Подключение…» под заголовком было правдой, а не догадкой.
	 */
	connection = $state<SocketPhase>('idle');

	/* --- Данные -------------------------------------------------------- */
	chats = $state<Chat[]>([]);
	/**
	 * Список чатов хотя бы раз доехал.
	 *
	 * Отличает «переписок нет» от «ещё не спрашивали». Без этого на запуске
	 * успевала мелькнуть надпись «Чатов пока нет» — в мессенджере она читается
	 * как потеря переписки, а не как ожидание.
	 */
	chatsLoaded = $state(false);

	/**
	 * Чаты загрузить не удалось.
	 *
	 * Отличается от «ещё едет»: ждать больше нечего, и человеку нужна кнопка,
	 * а не вечный скелет. На слабой связи это обычное дело.
	 */
	startFailed = $state(false);
	messagesByChat = $state<Record<string, Message[]>>({});
	/** Ревизия ветки комментариев, чтобы открытая панель обновлялась по WebSocket. */
	channelCommentRevisions = $state<Record<string, number>>({});
	pinnedMessagesByChat = $state<Record<string, Message[]>>({});
	users = $state<User[]>([]);
	/**
	 * Важные рассылки, которые ещё не показаны карточкой. Обычные рассылки
	 * живут в чате «ANDRESOT» и в эту очередь не попадают.
	 */
	announcements = $state<ServiceAnnouncement[]>([]);

	/** История звонков, загруженная из API и дополненная live-событиями. */
	calls = $state<CallRecord[]>([]);
	/**
	 * Идущие звонки по чатам — те, о которых сервер нам сообщил.
	 *
	 * В журнале (`calls`) лежат только законченные, а экран звонка знает лишь
	 * тот, что открыт здесь. Групповой же звонок живёт и без нас: отклонил или
	 * выпал — он продолжается, и чату нужно знать о нём, чтобы дать вернуться.
	 */
	ongoingCalls = $state<Record<string, ServerCall>>({});
	callsAvailable = $state(false);
	callsRecordingAvailable = $state(false);

	/** Идёт ли первичная загрузка — от неё зависит показ заглушек. */
	loading = $state(true);

	/** Переписки, которые грузятся впервые, и те, что загрузить не вышло. */
	historyLoading = $state<Record<string, true>>({});
	historyFailed = $state<Record<string, true>>({});

	/** Чаты, для которых переписка уже загружена: второй раз не тянем. */
	private loaded = new Set<string>();
	private loadedPinned = new Set<string>();
	private lifecycle = 0;
	private unsubscribeMessage: (() => void) | null = null;
	private unsubscribePhase: (() => void) | null = null;
	private unwatchScreen: (() => void) | null = null;
	private chatSnapshot: Map<string, Partial<Chat> | null> | null = null;
	private reconcileRequest = 0;
	/** При запуске доехало не всё (папки, контакты) — добрать при первой же связи. */
	private startIncomplete = false;

	/**
	 * Когда каждый открытый чат сверялся в последний раз — по часам сервера.
	 *
	 * Отсюда берётся «покажи, что изменилось с тех пор». Не $state: на экране
	 * это не видно, а лишние перерисовки списку сообщений ни к чему.
	 */
	private syncedAt: Record<string, number> = {};
	/** Отложенная запись на устройство: см. `scheduleSave`. */
	private saveTimer: ReturnType<typeof setTimeout> | null = null;
	/** Таймеры отметок «печатает…»: ключ — чат и человек. */
	private typingTimers = new Map<string, ReturnType<typeof setTimeout>>();
	/** Когда мы сами в последний раз сообщили о наборе — по чатам. */
	private typingSentAt = new Map<string, number>();

	/**
	 * Учётная запись целиком — такой, какой её отдал сервер.
	 *
	 * Самому мессенджеру она не нужна, ему хватает `me`. Лежит здесь ради
	 * запуска без сети: тогда спросить сервер, кто вошёл, не у кого, а
	 * показывать переписку без имени и аватара — половина дела.
	 */
	account: AccountUser | null = null;
	/** Что делать при отказе в доступе: нужно, чтобы повтор запуска шёл тем же путём. */
	private onUnauthorized: (() => void) | undefined;
	private recordingReadyFor = new Set<string>();
	private mediaConnections = new Map<string, Promise<void>>();
	private callRevisions = new Map<string, number>();
	/** Не открывать заново вызов из запоздавшего call.state после своей трубки. */
	private locallyEndingCallIds = new Set<string>();
	/** Звонки этой вкладки: начатые, принятые или открытые по ссылке здесь. */
	private callsHere = new Set<string>(readCallsHere());

	/* --- Звонок -------------------------------------------------------- */
	activeCall = $state<ActiveCall | null>(null);
	/** Панель звонка свёрнута в угол, интерфейс мессенджера доступен. */
	callMinimized = $state(false);
	/** Что открыто сбоку от звонка: участники, переписка или ничего. */
	callSidePanel = $state<CallSidePanel>('none');
	/** Сетка участников или один крупно. */
	callViewMode = $state<CallViewMode>('grid');
	/** Кто показан крупно в режиме «один». */
	callFocusedId = $state<string | null>(null);

	/* --- Черновики ------------------------------------------------------ */
	private drafts = $state<Record<string, string>>({});
	draftHydrated = $state(false);
	draftStorageAvailable = $state(true);
	private draftSaveTimer: ReturnType<typeof setTimeout> | null = null;

	/* --- Ответ на сообщение --------------------------------------------- */
	replyTarget = $state<Message | null>(null);
	/** Сообщение, текст которого сейчас редактируется в composer. */
	editTarget = $state<Message | null>(null);
	/** Множественный выбор живёт только в текущем чате. */
	selectedMessageIds = $state<string[]>([]);
	messageSelectionChatId = $state<string | null>(null);
	/** Сообщение, к которому нужно прокрутить ленту после загрузки поискового контекста. */
	messageFocusId = $state<string | null>(null);

	/* =====================================================================
	   Производные значения
	   ===================================================================== */

	/** Выбранный чат целиком. */
	selectedChat = $derived(this.chats.find((c) => c.id === this.selectedChatId) ?? null);

	/** Сообщения выбранного чата. */
	messages = $derived(this.selectedChatId ? (this.messagesByChat[this.selectedChatId] ?? []) : []);

	/** Папки в порядке показа. Встроенная «Все» всегда первая. */
	sortedFolders = $derived(
		[...this.folders].sort((a, b) => {
			if (a.isBuiltIn !== b.isBuiltIn) return a.isBuiltIn ? -1 : 1;
			return a.order - b.order;
		})
	);

	activeFolder = $derived(
		this.folders.find((f) => f.id === this.activeFolderId) ?? this.sortedFolders[0] ?? null
	);

	/**
	 * Список чатов для панели: сначала фильтр по активной папке, затем поиск,
	 * затем сортировка — закреплённые сверху, внутри групп по времени
	 * последнего сообщения.
	 *
	 * Поиск идёт по всем чатам, а не только по текущей папке: человек, который
	 * ищет собеседника по имени, обычно не помнит, в какую папку тот попал.
	 */
	visibleChats = $derived.by(() => {
		const query = this.searchQuery.trim().toLowerCase();

		const base = query
			? this.chats.filter(
					(c) =>
						c.title.toLowerCase().includes(query) ||
						(c.lastMessage?.text ?? '').toLowerCase().includes(query)
				)
			: this.chats.filter((c) => chatMatchesFolder(c, this.activeFolder, this.contactIds));

		return [...base].sort((a, b) => {
			if (a.isPinned !== b.isPinned) return a.isPinned ? -1 : 1;

			const aTime = a.lastMessage?.createdAt ?? 0;
			const bTime = b.lastMessage?.createdAt ?? 0;
			return bTime - aTime;
		});
	});

	/** Суммарное число непрочитанных — для счётчика на рейле навигации. */
	totalUnread = $derived(this.chats.reduce((sum, c) => sum + (c.isMuted ? 0 : c.unreadCount), 0));

	/**
	 * Непрочитанное в конкретной папке — счётчик на её вкладке.
	 * Беззвучные чаты не считаются: они не должны требовать внимания.
	 */
	unreadInFolder(folder: ChatFolder): number {
		return this.chats
			.filter((c) => chatMatchesFolder(c, folder, this.contactIds))
			.reduce((sum, c) => sum + (c.isMuted ? 0 : c.unreadCount), 0);
	}

	/** Попадает ли чат в папку — нужно экранам настроек для подсчётов. */
	chatInFolder(chat: Chat, folder: ChatFolder): boolean {
		return chatMatchesFolder(chat, folder, this.contactIds);
	}

	/**
	 * Обновить список контактов после изменения в панели контактов.
	 *
	 * Без этого добавленный контакт попадал в папку «Контакты» только после
	 * перезапуска приложения: список берётся один раз при входе, а панель
	 * контактов держит свою копию.
	 */
	setContacts(contacts: { userId: string; alias?: string; aliasLastName?: string }[]): void {
		this.contactIds = new Set(contacts.map((contact) => contact.userId));
		this.contactNames = Object.fromEntries(
			contacts
				.map((contact) => [
					contact.userId,
					[contact.alias, contact.aliasLastName].filter(Boolean).join(' ')
				])
				.filter(([, name]) => name)
		);
	}

	/**
	 * Как показывать чат: у личной переписки — имя из своей книжки, если оно
	 * задано. Сервер знает человека под его собственным именем и знать про
	 * вашу запись не должен, поэтому подмена происходит здесь.
	 */
	displayTitle(chat: Chat): string {
		// «Избранное» названо не именем человека, а тем, чем оно является:
		// в списке чатов собственное имя выглядело бы как чужой диалог.
		if (chat.isSaved) return 'Избранное';
		if (chat.kind !== 'dialog' || !chat.peerId) return chat.title;
		return this.contactNames[chat.peerId] ?? chat.title;
	}

	/**
	 * Открыть «Избранное», заведя его при первом обращении.
	 *
	 * Чат заводится не при регистрации, а по надобности: пустая строка в
	 * списке у того, кто ею не пользуется, — лишний шум.
	 */
	async ensureSaved(): Promise<string> {
		const known = this.chats.find((chat) => chat.isSaved);
		if (known) return known.id;

		const chatId = await createDialog(this.me.id);
		this.adoptChats(await fetchChats());
		this.scheduleSave();
		return chatId;
	}

	/** Открыть «Избранное», заведя его при первом обращении. */
	async openSaved(): Promise<void> {
		this.selectChat(await this.ensureSaved());
	}

	/** Чат «Избранное», если он уже заведён. */
	savedChat = $derived(this.chats.find((chat) => chat.isSaved) ?? null);

	/** Только ещё не просмотренные пропущенные — для бейджа раздела звонков. */
	missedCalls = $derived(
		this.calls.filter((call) => !call.isOutgoing && call.outcome === 'missed' && !call.viewedAt)
			.length
	);

	/* =====================================================================
	   Действия
	   ===================================================================== */

	/* =====================================================================
	   Связь с сервером
	   ===================================================================== */

	/**
	 * Загрузить состояние и подключиться. Вызывается после входа.
	 *
	 * Начальное состояние берётся обычными запросами, а не по соединению:
	 * так приложение открывается даже если соединение установиться не смогло —
	 * переписку хотя бы можно прочитать.
	 */
	async start(me: User, onUnauthorized?: () => void): Promise<void> {
		this.stop();
		const lifecycle = ++this.lifecycle;
		this.me = me;
		this.onUnauthorized = onUnauthorized;
		this.loading = true;

		/*
		 * Сохранённое на устройстве показываем сразу, не дожидаясь сервера.
		 *
		 * Раньше каждый запуск начинался со скелета: список чатов есть только
		 * в памяти вкладки, и на слабой связи человек несколько секунд смотрел
		 * на серые полосы вместо своей переписки. Всё, что придёт с сервера,
		 * ляжет поверх — ниже по этому же методу.
		 */
		await this.restoreFromCache(me, lifecycle);
		const storedDrafts = await readDrafts(me.id);
		if (lifecycle !== this.lifecycle) return;
		this.draftStorageAvailable = storedDrafts !== null;
		this.drafts = storedDrafts ?? {};
		this.draftHydrated = true;
		for (const chat of this.chats) chat.draft = this.drafts[chat.id] || undefined;

		try {
			/*
			 * Контакты берём здесь же: без них правила папок «Контакты» и
			 * «Не контакты» молча считали бы всех незнакомцами. Отдельным
			 * запросом позже это дало бы папку, которая при запуске пустая
			 * и наполняется через секунду.
			 *
			 * Не дошедшее не роняет запуск целиком. Раньше шесть запросов шли
			 * вместе, и любой из них — скажем, список звонков — оставлял
			 * человека на слабой связи с пустым экраном навсегда. Без чатов
			 * показывать нечего, поэтому только они обязательны; остальное
			 * подождёт до следующей сверки.
			 */
			const orNothing = <T>(promise: Promise<T>): Promise<T | null> => promise.catch(() => null);

			const [chats, users, folders, contacts, callSnapshot, announcements] = await Promise.all([
				fetchChats(),
				orNothing(fetchUsers()),
				orNothing(folderApi.fetchFolders()),
				orNothing(fetchContacts()),
				orNothing(callApi.fetchCalls()),
				orNothing(claimAnnouncements())
			]);
			if (lifecycle !== this.lifecycle) return;

			this.chats = chats;
			for (const chat of this.chats) chat.draft = this.drafts[chat.id] || undefined;
			this.chatsLoaded = true;
			this.startFailed = false;
			/*
			 * Не дошедшее не затирает то, что уже есть. Раньше на его место ложилась
			 * пустота: имена и папки, только что показанные из сохранённого, пропадали,
			 * пустота же записывалась на устройство, а кнопки звонка молча переставали
			 * работать до перезагрузки. Недостающее добирает следующая сверка.
			 */
			if (users) this.users = users;
			if (folders) this.folders = folders;
			if (contacts) this.setContacts(contacts);
			if (callSnapshot) this.adoptCallSnapshot(callSnapshot);
			this.startIncomplete = !users || !folders || !contacts || !callSnapshot;
			for (const announcement of announcements ?? []) this.showAnnouncement(announcement);
			if (!this.folders.some((folder) => folder.id === this.activeFolderId)) {
				this.activeFolderId =
					this.folders.find((folder) => folder.isBuiltIn)?.id ?? this.folders[0]?.id ?? '';
			}

			// Открываем первый чат, чтобы приложение не встречало пустотой.
			const first = chats[0];
			if (first && !this.selectedChatId) {
				this.selectedChatId = first.id;
				await this.loadMessages(first.id);
			}

			this.scheduleSave();
		} catch {
			/*
			 * Ждать больше нечего — покажем кнопку вместо вечного скелета.
			 * Отказ здесь никто не ловил: вызов стоит через `void`, и человек
			 * оставался со скелетом списка навсегда, без объяснения.
			 *
			 * Если на устройстве что-то сохранено, оно уже на экране — тогда
			 * кнопка не нужна: показывать её поверх переписки значило бы
			 * пугать без повода.
			 *
			 * Соединение всё равно поднимаем ниже: когда связь появится, сверка
			 * принесёт чаты сама, и нажимать ничего не придётся.
			 */
			if (lifecycle === this.lifecycle && !this.chatsLoaded) this.startFailed = true;
		} finally {
			if (lifecycle === this.lifecycle) this.loading = false;
		}

		if (lifecycle !== this.lifecycle) return;

		this.unsubscribeMessage = socket.onMessage((message) => this.applyEvent(message));

		// Подписка не отдаёт текущее состояние, только следующие изменения,
		// поэтому первое значение берём сами — иначе до первой смены фазы
		// заголовок уверял бы, что связи нет.
		this.connection = socket.phase;

		this.unsubscribePhase = socket.onPhase((phase) => {
			this.connection = phase;

			// После восстановления связи догоняем пропущенное: пока её не было,
			// в чатах могли появиться сообщения.
			if (phase === 'open') {
				// Даже быстрый вход оставляет щель между REST-снимком и WebSocket.
				void this.reconcileAfterReconnect().then((reconciled) => {
					if (reconciled && lifecycle === this.lifecycle) this.flushOutbox();
				});
				// Звонок мог начаться или закончиться, пока соединения не было: событие
				// об этом до отключённого устройства не доходит.
				void this.resyncCalls();
				if (this.startIncomplete) void this.completeStart();
			}
			if (phase === 'unauthorized') onUnauthorized?.();
		});

		/*
		 * Возвращение к приложению отмечает открытый чат прочитанным: пока оно
		 * было свёрнуто, пришедшее намеренно не отмечалось.
		 */
		this.unwatchScreen?.();
		this.unwatchScreen = watchOnScreen(this.handleVisibility);

		socket.connect();
	}

	/** Повторить запуск, который не удался. */
	async retryStart(): Promise<void> {
		await this.start(this.me, this.onUnauthorized);
	}

	/**
	 * Открыть приложение по сохранённому на устройстве, без единого запроса.
	 *
	 * Для запуска без сети: сервер не отвечает, кто мы, — но это и не нужно,
	 * последний вид приложения лежит здесь же. Соединение не поднимаем: без
	 * короткого токена оно объявило бы отказ в доступе и выбросило человека
	 * на экран входа. Как только связь появится, вход пройдёт обычным путём и
	 * запустит `start`.
	 */
	async startFromCache(me: User): Promise<boolean> {
		this.stop();
		const lifecycle = ++this.lifecycle;
		this.me = me;

		const restored = await this.restoreFromCache(me, lifecycle);
		if (lifecycle === this.lifecycle) this.loading = false;

		return restored;
	}

	/** Положить сохранённое на экран. `false` — сохранённого нет. */
	private async restoreFromCache(me: User, lifecycle: number): Promise<boolean> {
		const saved = await readSnapshot(me.id);
		if (!saved || lifecycle !== this.lifecycle) return false;

		this.chats = saved.chats;
		// «Печатает…» из прошлого запуска снимать уже некому — таймеры умерли со страницей.
		for (const chat of this.chats) delete chat.typingUserIds;
		this.users = saved.users.some((user) => user.id === me.id) ? saved.users : [...saved.users, me];
		this.folders = saved.folders;
		this.activeFolderId = saved.activeFolderId;
		this.messagesByChat = saved.messages;
		this.syncedAt = saved.syncedAt;
		this.selectedChatId = saved.selectedChatId;

		/*
		 * Раз переписка на экране, помечаем её загруженной: иначе открытие
		 * чата снова пошло бы за всей историей, ради которой всё и затевалось.
		 * Недостающее принесёт догон — он спрашивает ровно то, что изменилось
		 * с прошлой сверки.
		 */
		for (const chatId of Object.keys(saved.messages)) this.loaded.add(chatId);
		this.chatsLoaded = true;
		this.startFailed = false;

		return true;
	}

	/**
	 * Отложенно сохранить нынешний вид приложения.
	 *
	 * Отложенно, потому что зовётся на каждое событие: при живой переписке
	 * запись шла бы по разу в секунду на сообщение. Полторы секунды тишины
	 * человек не замечает, а записей становится в разы меньше.
	 */
	private scheduleSave(): void {
		if (!this.me.id || this.saveTimer !== null) return;

		this.saveTimer = setTimeout(() => {
			this.saveTimer = null;
			void this.saveNow();
		}, 1500);
	}

	private async saveNow(): Promise<boolean> {
		if (!this.me.id) return false;

		/*
		 * `$state.snapshot` — не украшение: наблюдаемое состояние Svelte это
		 * Proxy, а браузер отказывается его сохранять («не удалось
		 * склонировать»). Отказ этот тихий — запись просто не появлялась.
		 */
		return writeSnapshot({
			userId: this.me.id,
			savedAt: Date.now(),
			account: this.account,
			chats: $state.snapshot(this.chats),
			users: $state.snapshot(this.users),
			folders: $state.snapshot(this.folders),
			activeFolderId: this.activeFolderId,
			selectedChatId: this.selectedChatId,
			// Только открытые переписки: остальных в памяти и нет.
			/*
			 * Сообщения с ещё не загруженными фотографиями не сохраняем: их
			 * снимки живут по ссылкам `blob:`, которые умирают вместе со
			 * страницей, и после перезапуска в переписке остались бы пустые
			 * рамки вместо фотографий.
			 */
			messages: Object.fromEntries(
				[...this.loaded].map((chatId) => [
					chatId,
					$state.snapshot(
						// На устройство — только хвост переписки: куски из глубины истории
						// при следующем запуске встали бы вплотную к нему, без разрыва.
						(this.messagesByChat[chatId] ?? []).filter(
							(message) =>
								!this.isUploading(message.id) &&
								message.seq >= (this.feedRanges[chatId]?.at(-1)?.lo ?? 0)
						)
					)
				])
			),
			syncedAt: { ...this.syncedAt }
		});
	}

	/** Чаты, у которых старое уже кончилось: повторно спрашивать незачем. */
	private historyDrained = new Set<string>();
	/** Чаты, для которых подгрузка идёт прямо сейчас. */
	private loadingOlder = new Set<string>();
	/*
	 * Куски переписки, известные целиком, — по номерам, от старых к новым.
	 *
	 * Лента не один сплошной отрезок: к последней сотне сообщений добавляются
	 * окрестности найденного поиском или закреплённого сообщения из глубины
	 * истории. Между такими кусками лежит ещё не загруженное. Раньше куски просто
	 * сливались в один список: сообщения стояли рядом, будто между ними ничего
	 * нет, а подгрузка «выше самого раннего» эту середину уже не доставала никогда.
	 *
	 * Последний кусок открыт сверху: в него попадает всё новое.
	 */
	private feedRanges = $state<Record<string, FeedRange[]>>({});

	/** Есть ли что подгружать выше — по этому лента решает, тянуть ли дальше. */
	hasOlder(chatId: string): boolean {
		return this.loaded.has(chatId) && !this.historyDrained.has(chatId);
	}

	/** Разрывы в ленте: после какого номера и перед каким лежит ещё не загруженное. */
	feedGaps(chatId: string): Array<{ after: number; before: number }> {
		const ranges = this.feedRanges[chatId] ?? [];
		return ranges.slice(1).map((range, index) => ({ after: ranges[index]!.hi, before: range.lo }));
	}

	/** Записать куски по порядку, слив пересекающиеся. */
	private setRanges(chatId: string, ranges: FeedRange[]): void {
		const merged: FeedRange[] = [];
		for (const range of [...ranges].sort((a, b) => a.lo - b.lo)) {
			const last = merged[merged.length - 1];
			if (last && range.lo <= last.hi) last.hi = Math.max(last.hi, range.hi);
			else merged.push({ lo: range.lo, hi: range.hi });
		}
		this.feedRanges[chatId] = merged;
	}

	/**
	 * Куски чата. Лента из памяти устройства кусков не помнит — она всегда хвост
	 * переписки, один кусок до самого конца.
	 */
	private rangesOf(chatId: string): FeedRange[] {
		const known = this.feedRanges[chatId];
		if (known && known.length > 0) return known;

		const first = (this.messagesByChat[chatId] ?? [])[0];
		this.feedRanges[chatId] = [{ lo: first?.seq ?? 0, hi: FEED_OPEN_END }];
		return this.feedRanges[chatId]!;
	}

	/** Добавить в ленту то, чего в ней ещё нет. Возвращает, сколько добавилось. */
	private absorb(chatId: string, incoming: Message[]): number {
		const list = this.messagesByChat[chatId] ?? [];
		const known = new Set(list.map((message) => message.id));
		const fresh = incoming.filter((message) => !known.has(message.id));
		if (fresh.length > 0) {
			this.messagesByChat[chatId] = [...list, ...fresh].sort((a, b) => a.seq - b.seq);
		}
		return fresh.length;
	}

	/**
	 * Подгрузить сотню сообщений ниже по номерам — выше по ленте.
	 *
	 * Без `before` — над самым ранним известным: так лента листается к началу
	 * переписки. С `before` — над куском, который с этого номера начинается:
	 * так закрывается разрыв, когда к нему подошли снизу.
	 *
	 * Возвращает, сколько сообщений добавилось: лента по этому числу решает,
	 * насколько подвинуть прокрутку, чтобы экран не прыгнул.
	 */
	async loadOlder(chatId: string, before?: number): Promise<number> {
		if (!this.loaded.has(chatId) || this.loadingOlder.has(chatId)) return 0;
		if ((this.messagesByChat[chatId] ?? []).length === 0) return 0;

		const ranges = this.rangesOf(chatId);
		const index = before === undefined ? 0 : ranges.findIndex((range) => range.lo === before);
		const target = ranges[index];
		if (!target) return 0;
		if (index === 0 && this.historyDrained.has(chatId)) return 0;
		const from = target.lo;

		this.loadingOlder.add(chatId);
		try {
			const older = await fetchMessages(chatId, { beforeSeq: from, limit: FEED_PAGE });
			const added = this.absorb(chatId, older);

			// Пока ждали ответа, ленту могли перечитать — куски берём заново.
			const now = this.rangesOf(chatId);
			const at = now.findIndex((range) => range.lo === from);
			if (at < 0) return added;

			const lowest = older.reduce((least, message) => Math.min(least, message.seq), from);
			// Неполная страница — ниже больше ничего нет.
			const exhausted = older.length < FEED_PAGE;
			if (exhausted && at === 0) this.historyDrained.add(chatId);
			// У куска посреди ленты «ниже ничего нет» значит, что разрыв закрыт:
			// дотягиваем его до предыдущего, и они сливаются.
			const floor = exhausted && at > 0 ? now[at - 1]!.hi : lowest;
			this.setRanges(
				chatId,
				now.map((range, position) => (position === at ? { lo: floor, hi: range.hi } : range))
			);
			return added;
		} catch {
			// Не вышло — лента попробует снова при следующей прокрутке.
			return 0;
		} finally {
			this.loadingOlder.delete(chatId);
		}
	}

	/**
	 * Подгрузить сотню сообщений сразу после куска, который кончается номером
	 * `after`, — так закрывается разрыв, когда к нему подошли сверху и читают вниз.
	 */
	async loadNewer(chatId: string, after: number): Promise<number> {
		if (!this.loaded.has(chatId) || this.loadingOlder.has(chatId)) return 0;

		const ranges = this.rangesOf(chatId);
		const index = ranges.findIndex((range) => range.hi === after);
		if (index < 0 || index === ranges.length - 1) return 0;

		this.loadingOlder.add(chatId);
		try {
			const newer = await fetchMessages(chatId, { afterSeq: after, limit: FEED_PAGE });
			const added = this.absorb(chatId, newer);

			const now = this.rangesOf(chatId);
			const at = now.findIndex((range) => range.hi === after);
			if (at < 0 || at === now.length - 1) return added;

			const highest = newer.reduce((most, message) => Math.max(most, message.seq), after);
			// Неполная страница — выше до самого следующего куска ничего нет.
			const ceiling = newer.length < FEED_PAGE ? now[at + 1]!.lo : highest;
			this.setRanges(
				chatId,
				now.map((range, position) => (position === at ? { lo: range.lo, hi: ceiling } : range))
			);
			return added;
		} catch {
			return 0;
		} finally {
			this.loadingOlder.delete(chatId);
		}
	}

	/** Обновить чат после Web Push, когда WebSocket мог быть отключён. */
	async refreshChat(chatId: string): Promise<void> {
		const [chats, messages] = await Promise.all([fetchChats(), fetchMessages(chatId)]);
		if (!chats.some((chat) => chat.id === chatId)) return;

		this.adoptChats(chats);
		this.mergeHistory(chatId, messages);
		this.loaded.add(chatId);
	}

	/**
	 * Положить перечитанную историю поверх имеющейся, ничего не потеряв.
	 *
	 * Перечитанное с сервера — не вся правда: рядом лежит своё, чего у сервера
	 * ещё нет. Это не только неотправленное. Сообщение может уйти уже после
	 * того, как перечитывание началось, — тогда в ответе его нет, а на экране
	 * оно есть, и простая замена списка стирала бы его на глазах. Такое
	 * случается ровно в тот момент, когда связь вернулась и очередь пошла:
	 * сверка и досылка идут почти одновременно.
	 *
	 * Признак «сервер про это ещё не знает» — номер больше последнего
	 * перечитанного: номера назначает сервер по возрастанию.
	 */
	private mergeHistory(chatId: string, fetched: Message[]): void {
		const newestSeq = fetched.at(-1)?.seq ?? 0;
		const oldestSeq = fetched[0]?.seq ?? 0;
		const current = this.messagesByChat[chatId] ?? [];
		const unsent = (message: Message) =>
			message.delivery === 'queued' ||
			message.delivery === 'sending' ||
			message.delivery === 'failed';
		// Докуда лента знала переписку до перечитывания — по подтверждённым сообщениям.
		const knownUpTo = current.reduce(
			(most, message) => (unsent(message) ? most : Math.max(most, message.seq)),
			0
		);
		const ranges = current.length > 0 ? this.rangesOf(chatId) : [];
		const fetchedIds = new Set(fetched.map((message) => message.id));

		const kept = current.filter((message) => {
			if (fetchedIds.has(message.id)) return false;
			if (unsent(message) || message.seq > newestSeq) return true;
			/*
			 * Старше перечитанного окна — прежние страницы и окрестности найденного.
			 * Они никуда не делись, и выбрасывать их значило бы выдернуть переписку
			 * из-под того, кто её читает. А вот то, чего нет внутри окна, сервер
			 * больше не отдаёт — этого на экране быть не должно.
			 */
			return fetched.length > 0 && message.seq < oldestSeq;
		});

		this.messagesByChat[chatId] = [...fetched, ...kept].sort((a, b) => a.seq - b.seq);

		if (fetched.length === 0) {
			this.setRanges(chatId, [{ lo: 0, hi: FEED_OPEN_END }]);
			return;
		}
		// Прежний последний кусок закрываем там, где он кончался: если с тех пор
		// пришло больше страницы, между ним и перечитанным окном остаётся разрыв.
		const previous = ranges
			.map((range) => (range.hi === FEED_OPEN_END ? { lo: range.lo, hi: knownUpTo } : range))
			.filter((range) => range.hi >= range.lo && range.lo < oldestSeq);
		this.setRanges(chatId, [...previous, { lo: oldestSeq, hi: FEED_OPEN_END }]);
	}

	stop(): void {
		this.lifecycle += 1;
		admin.reset();

		/*
		 * Отложенную запись отменяем. Иначе она сработала бы уже после
		 * очистки ниже и сохранила пустоту вместо переписки — а при выходе
		 * запись всё равно стирается целиком.
		 */
		if (this.saveTimer) clearTimeout(this.saveTimer);
		this.saveTimer = null;
		if (this.draftSaveTimer) clearTimeout(this.draftSaveTimer);
		this.draftSaveTimer = null;

		this.unwatchScreen?.();
		this.unwatchScreen = null;

		this.unsubscribeMessage?.();
		this.unsubscribePhase?.();
		this.unsubscribeMessage = null;
		this.unsubscribePhase = null;
		socket.disconnect();
		// Подписки уже нет, а сокет объявит `idle` только сейчас — сбрасываем
		// сами, иначе после выхода останется висеть чужое «Подключение…».
		this.connection = 'idle';

		this.chats = [];
		this.chatsLoaded = false;
		this.startFailed = false;
		this.users = [];
		this.announcements = [];
		this.calls = [];
		this.ongoingCalls = {};
		this.callsAvailable = false;
		this.callsRecordingAvailable = false;
		this.folders = [];
		this.activeFolderId = '';
		this.contactIds = new Set();
		this.contactNames = {};
		this.messagesByChat = {};
		this.pinnedMessagesByChat = {};
		this.feedRanges = {};
		this.historyDrained.clear();
		this.loaded.clear();
		this.loadedPinned.clear();
		this.syncedAt = {};
		for (const timer of this.typingTimers.values()) clearTimeout(timer);
		this.typingTimers.clear();
		this.typingSentAt.clear();
		this.selectedChatId = null;
		this.drafts = {};
		this.draftHydrated = false;
		this.draftStorageAvailable = true;
		this.replyTarget = null;
		this.editTarget = null;
		this.selectedMessageIds = [];
		this.messageSelectionChatId = null;
		this.messageFocusId = null;
		this.infoPanelOpen = false;
		this.activeCall = null;
		this.preparedCall = null;
		this.recordingReadyFor.clear();
		this.mediaConnections.clear();
		this.callRevisions.clear();
		this.locallyEndingCallIds.clear();
		void callMedia.disconnect();
		this.loading = false;
		this.chatSnapshot = null;
		this.startIncomplete = false;
	}

	/** Догон после обрыва: говорим серверу, что у нас есть, он досылает остальное. */
	private async catchUp(): Promise<void> {
		const lifecycle = this.lifecycle;
		const cursors = [...this.loaded].map((chatId) => {
			/*
			 * Своё неотправленное в счёт не идёт. Номер ему придуман наперёд, и
			 * если сообщение до сервера не дошло, догон с таким номером
			 * перешагнул бы через чужое сообщение с тем же номером — оно
			 * просто не приехало бы.
			 */
			const list = (this.messagesByChat[chatId] ?? []).filter(
				(message) =>
					message.delivery !== 'queued' &&
					message.delivery !== 'sending' &&
					message.delivery !== 'failed'
			);
			const last = list[list.length - 1];

			return { chatId, seq: last?.seq ?? 0, since: this.syncedAt[chatId] };
		});

		if (cursors.length === 0) return;

		try {
			const ack = await socket.request('sync.pull', { cursors });
			if (lifecycle !== this.lifecycle) return;

			/*
			 * Время следующей сверки — серверное. Своё подставить нельзя: часы
			 * устройства врут на минуты, и сверка либо теряла бы изменения,
			 * либо тащила лишнее.
			 */
			const syncedAt = ack.op === 'ack' ? ack.payload.syncedAt : undefined;
			if (syncedAt !== undefined) {
				for (const cursor of cursors) this.syncedAt[cursor.chatId] = syncedAt;
			}
		} catch {
			// Не вышло — переподключение попробует снова.
		}
	}

	/**
	 * Сверка после настоящего обрыва связи.
	 *
	 * Раньше здесь перечитывалась вся история каждого открытого чата: новое
	 * догонялось по номеру, а правки и удаления номер сохраняют и по номеру не
	 * находились. На дёрганой связи переподключения идут одно за другим, и
	 * каждое запускало самый тяжёлый запрос приложения — как раз тогда, когда
	 * связи и так нет.
	 *
	 * Теперь переписки догоняются разницей: сервер отдаёт изменившееся с
	 * прошлой сверки. Целиком перечитывается только то, что в один ответ не
	 * поместилось.
	 */
	private async reconcileAfterReconnect(): Promise<boolean> {
		const lifecycle = this.lifecycle;
		const request = ++this.reconcileRequest;
		const current = () => lifecycle === this.lifecycle && request === this.reconcileRequest;
		const changes = new SvelteMap<string, Partial<Chat> | null>();
		this.chatSnapshot = changes;
		try {
			const [chats, users] = await Promise.all([fetchChats(), fetchUsers()]);
			// Пока ждали, могли выйти, переподключиться или получить новые события.
			if (!current()) return false;
			const merged = new SvelteMap(chats.map((chat) => [chat.id, chat]));
			for (const [id, patch] of changes) {
				const base = merged.get(id) ?? this.chats.find((chat) => chat.id === id);
				if (patch && base) merged.set(id, { ...base, ...patch });
				else if (!patch) merged.delete(id);
			}
			this.adoptChats([...merged.values()]);
			this.users = users;
			// Связь вернулась и чаты приехали — кнопка повтора больше не нужна.
			this.chatsLoaded = true;
			this.startFailed = false;

			if (this.selectedChatId && !this.chats.some((chat) => chat.id === this.selectedChatId)) {
				this.selectedChatId = this.chats[0]?.id ?? null;
			}

			this.scheduleSave();
		} catch {
			// Следующий reconnect повторит сверку; локальные данные не стираем.
		} finally {
			if (this.chatSnapshot === changes) this.chatSnapshot = null;
		}

		if (!current()) return false;
		await this.catchUp();
		if (!current()) return false;
		// Пока связи не было, собеседник мог написать в открытый чат: сервер вернул
		// его непрочитанным, хотя новые сообщения уже перед глазами.
		if (this.selectedChatId) this.markReadIfVisible(this.selectedChatId);
		return true;
	}

	/**
	 * Перечитать переписку целиком.
	 *
	 * Запасной путь на случай, когда разница не помещается в один ответ:
	 * пропустить остаток хуже, чем один раз забрать всё.
	 */
	private async reloadHistory(chatId: string): Promise<void> {
		try {
			this.mergeHistory(chatId, await fetchMessages(chatId));
		} catch {
			// Следующая сверка повторит.
		}
	}

	/** Загрузить переписку чата, если её ещё нет. */
	/**
	 * Повторить загрузку переписки, которая не открылась.
	 */
	retryHistory(chatId: string): void {
		void this.loadMessages(chatId).then(() => this.markReadIfVisible(chatId));
	}

	private async loadMessages(chatId: string): Promise<void> {
		if (this.loaded.has(chatId) && this.loadedPinned.has(chatId)) return;

		const first = !this.loaded.has(chatId);
		if (first) {
			this.historyLoading[chatId] = true;
			delete this.historyFailed[chatId];
		}
		try {
			const [messages, pinned] = await Promise.all([
				this.loaded.has(chatId)
					? Promise.resolve(this.messagesByChat[chatId] ?? [])
					: fetchMessages(chatId),
				this.loadedPinned.has(chatId)
					? Promise.resolve(this.pinnedMessagesByChat[chatId] ?? [])
					: fetchPinnedMessages(chatId)
			]);
			/*
			 * Закреплённое в ленту не подмешиваем. Оно бывает из глубины истории,
			 * и раньше вставало первым в списке вплотную к последней сотне — будто
			 * между ними ничего нет. К нему ведёт своя строка над лентой.
			 */
			if (first) {
				this.mergeHistory(chatId, messages);
				this.historyDrained.delete(chatId);
			}
			this.pinnedMessagesByChat[chatId] = pinned;
			this.loaded.add(chatId);
			this.loadedPinned.add(chatId);
			this.scheduleSave();
		} catch {
			if (!this.loaded.has(chatId)) {
				this.messagesByChat[chatId] = [];
				// Пустая лента здесь — не «в чате ничего нет», а «не дошло»: об этом
				// и надо сказать, вместо приглашения написать первое сообщение.
				this.historyFailed[chatId] = true;
			}
			if (!this.loadedPinned.has(chatId)) this.pinnedMessagesByChat[chatId] = [];
		} finally {
			if (first) delete this.historyLoading[chatId];
		}
	}

	/**
	 * Применить событие с сервера — и запомнить итог на устройстве.
	 *
	 * Обёртка нужна из-за разбора ниже: у него на каждую ветку свой `return`,
	 * а сохранять надо после любой.
	 */
	private applyEvent(event: ServerMessage): void {
		const payload = event.payload;
		const id =
			'chat' in payload
				? payload.chat.id
				: 'chatId' in payload
					? payload.chatId
					: event.op === 'message.new' || event.op === 'message.updated'
						? event.payload.message.chatId
						: undefined;
		const before =
			this.chatSnapshot && id
				? $state.snapshot(this.chats.find((chat) => chat.id === id))
				: undefined;
		this.handleEvent(event);
		// Накладываем только изменения события: неизменённые поля добирает REST.
		// Повторно событие не исполняем — звук и счётчики должны сработать один раз.
		if (this.chatSnapshot && id) {
			const chat = this.chats.find((item) => item.id === id);
			if (chat) {
				const after = $state.snapshot(chat);
				const patch = this.chatSnapshot.get(id) ?? {};
				for (const key of Object.keys(after) as (keyof Chat)[]) {
					if (
						!before ||
						'chat' in payload ||
						JSON.stringify(before[key]) !== JSON.stringify(after[key])
					) {
						Object.assign(patch, { [key]: after[key] });
					}
				}
				if (Object.keys(patch).length) this.chatSnapshot.set(id, patch);
			} else if (
				event.op === 'chat.deleted' ||
				(event.op === 'chat.member.left' && event.payload.userId === this.me.id)
			) {
				this.chatSnapshot.set(id, null);
			}
		}
		this.scheduleSave();
	}

	private handleEvent(event: ServerMessage): void {
		switch (event.op) {
			case 'message.new': {
				this.applyNewMessage(event.payload.message);
				if (event.payload.message.authorId !== this.me.id) {
					const message = event.payload.message;
					const chat = this.chats.find((item) => item.id === message.chatId);
					if (
						window.andresotDesktop &&
						prefs.notifyMessages &&
						!prefs.isQuietNow() &&
						(!chat?.isMuted || (chat.mutedUntil !== undefined && chat.mutedUntil < Date.now())) &&
						!this.reading(message.chatId)
					) {
						window.andresotDesktop.notify({
							title: chat?.title ?? 'Новое сообщение',
							body: prefs.notifyPreview ? message.text || 'Вложение' : 'Новое сообщение',
							// Окно перед глазами — звук подаёт сама страница (MessageSound.svelte);
							// второй, системный, поверх него не нужен. Выключенный звук тоже чтим.
							silent: this.onScreen() || !prefs.shouldPlayNotificationSound('messages'),
							// Оболочка до 1.0.8 по щелчку перезагружает страницу. Во время звонка
							// это оборвало бы разговор — там окно только показываем. Новая
							// открывает чат на месте (`opensInPage`), ей адрес нужен всегда.
							...(this.activeCall && !window.andresotDesktop.opensInPage
								? {}
								: { path: `/?section=chats&chat=${encodeURIComponent(message.chatId)}` })
						});
					}
					messageSound.emit({
						messageId: event.payload.message.id,
						chatId: event.payload.message.chatId
					});
				}
				return;
			}

			case 'message.updated': {
				this.applyUpdatedMessage(event.payload.message);
				return;
			}

			case 'chat.updated':
			case 'chat.member.joined':
			case 'chat.member.updated': {
				this.applyChatUpdate(event.payload.chat);
				if (event.op === 'chat.member.joined') void this.refreshUsers();
				return;
			}

			case 'chat.member.left': {
				if (event.payload.userId === this.me.id) {
					this.removeChatLocally(event.payload.chat.id);
				} else {
					this.applyChatUpdate(event.payload.chat);
				}
				return;
			}

			case 'chat.deleted': {
				this.removeChatLocally(event.payload.chatId);
				return;
			}

			case 'chat.history.cleared': {
				this.messagesByChat[event.payload.chatId] = [];
				delete this.feedRanges[event.payload.chatId];
				this.historyDrained.delete(event.payload.chatId);
				this.loaded.delete(event.payload.chatId);
				this.loadedPinned.delete(event.payload.chatId);
				void this.loadMessages(event.payload.chatId);
				return;
			}

			case 'channel.comment.created': {
				const postId = event.payload.comment.postId;
				this.channelCommentRevisions[postId] = (this.channelCommentRevisions[postId] ?? 0) + 1;
				return;
			}

			case 'channel.comment.updated': {
				const postId = event.payload.comment.postId;
				this.channelCommentRevisions[postId] = (this.channelCommentRevisions[postId] ?? 0) + 1;
				return;
			}

			case 'channel.comment.deleted': {
				const postId = event.payload.postId;
				this.channelCommentRevisions[postId] = (this.channelCommentRevisions[postId] ?? 0) + 1;
				return;
			}

			case 'channel.join-requests.updated': {
				const chat = this.chats.find((item) => item.id === event.payload.chatId);
				if (chat) chat.pendingJoinRequestCount = event.payload.pendingCount;
				return;
			}

			case 'sync.push': {
				const { chatId, messages, truncated } = event.payload;

				/*
				 * Догон приносит не только новое, но и изменившееся: правку,
				 * удаление, реакцию, голос в опросе. Раньше здесь брались лишь
				 * незнакомые сообщения, и правка, случившаяся во время обрыва,
				 * оставалась невидимой до перезагрузки страницы.
				 */
				const arrived = new Map(messages.map((message) => [message.id, message]));
				const list = (this.messagesByChat[chatId] ?? []).map(
					(message) => arrived.get(message.id) ?? message
				);
				const known = new Set(list.map((m) => m.id));

				this.messagesByChat[chatId] = [...list, ...messages.filter((m) => !known.has(m.id))].sort(
					(a, b) => a.seq - b.seq
				);

				const chat = this.chats.find((c) => c.id === chatId);
				const last = messages[messages.length - 1];

				// Последним в чате объявляем только то, что и правда последнее:
				// в догоне может приехать правка старого сообщения.
				if (chat && last && last.seq >= chat.lastSeq) {
					chat.lastSeq = Math.max(chat.lastSeq, last.seq);
					chat.lastMessage = last;
					chat.unreadCount = Math.max(0, chat.lastSeq - chat.lastReadSeq);
					// Догон пришёл в чат, который сейчас читают, — он прочитан.
					this.markReadIfVisible(chatId);
				}

				// Разницы оказалось больше, чем помещается в ответ, — остаток
				// безопаснее перечитать целиком, чем потерять молча.
				if (truncated) void this.reloadHistory(chatId);

				return;
			}

			case 'chat.typing': {
				// Отдельного «перестал печатать» в протоколе нет намеренно:
				// событие всё равно теряется при обрыве, и отметка зависала бы.
				// Проще снимать её по времени.
				if (event.payload.userId !== this.me.id) {
					this.setTyping(event.payload.chatId, event.payload.userId, true);
				}
				return;
			}

			case 'chat.read': {
				const chat = this.chats.find((c) => c.id === event.payload.chatId);
				if (!chat || event.payload.userId === this.me.id) return;

				/*
				 * Собеседник дочитал. Двигаем одно число, а не метку на каждом
				 * сообщении: галочка из него выводится, и такой отметке нечему
				 * потеряться при следующей загрузке списка.
				 *
				 * Только вперёд: события от разных участников приходят вперемешку,
				 * и отставший не должен отматывать галочки назад.
				 */
				chat.peersReadSeq = Math.max(chat.peersReadSeq, event.payload.seq);

				return;
			}

			case 'presence': {
				const user = this.users.find((u) => u.id === event.payload.userId);
				if (!user) return;

				user.presence = event.payload.online ? 'online' : 'offline';
				if (event.payload.lastSeenAt) user.lastSeenAt = event.payload.lastSeenAt;

				return;
			}

			case 'call.invite':
			case 'call.ringing':
			case 'call.accept':
			case 'call.decline':
			case 'call.state': {
				const call = event.payload.call;
				if (!this.acceptCallRevision(call)) return;
				this.upsertCallRecord(call);
				if (this.locallyEndingCallIds.has(call.id)) return;
				const me = call.participants.find((participant) => participant.userId === this.me.id);
				if (!me || call.phase === 'ended') return;
				if (!this.callConcernsThisTab(call)) {
					// На звонок ответили — или сами звонят — с другого устройства этого же
					// человека. Здесь перестаём звонить и в разговор не входим.
					if (this.activeCall?.id === call.id) this.dismissCall(call.id);
					return;
				}
				const here = this.callsHere.has(call.id);
				const ringing = me.state === 'ringing' && call.initiatorId !== this.me.id && !me.acceptedAt;
				if (
					event.op === 'call.invite' &&
					ringing &&
					window.andresotDesktop &&
					prefs.notifyCalls &&
					!prefs.isQuietNow()
				) {
					/*
					 * Без адреса: щелчок по уведомлению с адресом перезагружает страницу в
					 * оболочке, а экран входящего уже открыт — окно достаточно показать.
					 * Условие раньше ждало состояния «приглашён», которого у вызываемого
					 * не бывает, и уведомление о звонке не появлялось вовсе.
					 */
					window.andresotDesktop.notify({
						title: 'Входящий звонок',
						body: 'Вам звонят в ANDRESOT',
						// Звонит сама страница — рингтоном, а не коротким системным сигналом.
						silent: true
					});
				}
				const previous = this.activeCall?.id === call.id ? this.activeCall : null;
				const waited =
					previous?.participants.find((participant) => participant.userId === this.me.id)?.state ===
					'invited';
				if (!this.activeCall || previous) {
					const next = this.activeFromServer(call, previous?.phase);
					if (previous?.phase === 'prejoin') {
						/*
						 * Экран подготовки — наше состояние, сервер о нём не знает. Любое
						 * событие звонка раньше заменяло его экраном вызова, а стоило
						 * кому-то войти — подключало камеру и микрофон без спроса.
						 */
						this.activeCall = { ...next, phase: 'prejoin' };
						this.preparedCall = call;
					} else {
						this.activeCall = next;
					}
					/* Переключение микрофона или камеры приходит обратно как call.state.
					 * Это обновление текущего звонка, а не просьба раскрыть его заново. */
					if (!previous) this.callMinimized = false;
				}
				if (this.activeCall?.id !== call.id || this.activeCall.phase === 'prejoin') return;
				if (here && waited && ringing) {
					// Организатор впустил из комнаты ожидания. Человек уже просил войти —
					// показывать ему «входящий звонок» и ждать второго нажатия незачем.
					void this.acceptCall();
					return;
				}
				if (
					here &&
					me.state === 'joined' &&
					call.phase === 'active' &&
					callMedia.connectedCallId !== call.id
				) {
					void this.connectMedia(call);
				}
				return;
			}

			case 'call.end': {
				const call = event.payload.call;
				if (!this.acceptCallRevision(call)) return;
				this.upsertCallRecord(call);
				const locallyEnded = this.locallyEndingCallIds.delete(call.id);
				this.releaseCall(call.id);
				if (this.section === 'calls') void this.viewMissedCalls();
				if (this.activeCall?.id === call.id) {
					if (locallyEnded) {
						callRingtone.cancel(call.id);
					} else if (this.activeCall.phase === 'outgoing' && !this.activeCall.isGroup) {
						callRingtone.unavailable(call.id);
					} else if (
						this.activeCall.phase === 'active' ||
						this.activeCall.phase === 'reconnecting' ||
						(call.acceptedAt && this.activeCall.phase === 'connecting')
					) {
						callRingtone.interrupted(call.id);
					} else {
						callRingtone.stop(call.id);
					}
					this.activeCall = null;
					this.preparedCall = null;
					this.callMinimized = false;
					void callMedia.disconnect();
				}
				void this.refreshChat(call.chatId).catch(() => undefined);
				return;
			}

			case 'call.history.deleted': {
				this.applyDeletedCallHistory(event.payload);
				return;
			}

			case 'service.maintenance':
				// Работы включили или сняли прямо сейчас — экран появляется и
				// пропадает без перезагрузки страницы.
				maintenance.apply(event.payload);
				return;

			case 'service.announcement':
				this.showAnnouncement(event.payload);
				return;

			case 'mail.new':
				/*
				 * Счётчик «Входящих» обновляется, не открывая почту: раньше о новом
				 * письме нельзя было узнать иначе как зайдя в раздел.
				 */
				mail.applyIncoming(event.payload.unread);
				toasts.show(`Письмо · ${event.payload.sender}: ${event.payload.subject}`);
				return;

			case 'video.published':
				toasts.show(
					`Новое видео · ${event.payload.video.source.title}: ${event.payload.video.title}`
				);
				return;
		}
	}

	/** Когда перечитывали список из-за незнакомого чата — чаще раза в пять секунд незачем. */
	private unknownChatReloadAt = 0;

	/**
	 * Достать чат, о котором приложение ещё не знает, и применить сообщение.
	 *
	 * Страховка на случай потерянного объявления: терять переписку из-за
	 * пропавшего события нельзя, а перечитывание списка стоит одного запроса.
	 */
	private async recoverUnknownChat(message: Message): Promise<void> {
		const now = Date.now();
		if (now - this.unknownChatReloadAt < 5_000) return;
		this.unknownChatReloadAt = now;

		try {
			this.adoptChats(await fetchChats());
		} catch {
			// Сети нет — следующее сообщение или переподключение повторят.
			return;
		}

		if (this.chats.some((chat) => chat.id === message.chatId)) this.applyNewMessage(message);
		this.scheduleSave();
	}

	/**
	 * Принять список чатов с сервера, вернув на место местные черновики.
	 *
	 * Черновики живут на устройстве, сервер о них не знает. Без этого после
	 * каждого обрыва связи из списка пропадала пометка о начатом сообщении.
	 */
	private adoptChats(chats: Chat[]): void {
		for (const chat of chats) chat.draft = this.drafts[chat.id] || undefined;
		this.chats = chats;
	}

	private applyChatUpdate(chat: Chat): void {
		chat.draft = this.drafts[chat.id] || undefined;
		const index = this.chats.findIndex((item) => item.id === chat.id);
		if (index < 0) this.chats = [chat, ...this.chats];
		else this.chats[index] = chat;
	}

	private removeChatLocally(chatId: string): void {
		this.chats = this.chats.filter((chat) => chat.id !== chatId);
		delete this.messagesByChat[chatId];
		delete this.pinnedMessagesByChat[chatId];
		delete this.feedRanges[chatId];
		this.historyDrained.delete(chatId);
		this.loaded.delete(chatId);
		this.loadedPinned.delete(chatId);
		if (this.selectedChatId === chatId) {
			this.selectedChatId = this.chats[0]?.id ?? null;
			this.infoPanelOpen = false;
		}
	}

	private async refreshUsers(): Promise<void> {
		try {
			this.users = await fetchUsers();
		} catch {
			// Снимок чата уже применён; имена обновятся при reconnect или reload.
		}
	}

	/**
	 * Смотрит ли человек на приложение прямо сейчас.
	 *
	 * На сервере видимость уже учитывается, чтобы не слать лишние уведомления;
	 * здесь она решает, честно ли ставить «прочитано».
	 */
	private onScreen(): boolean {
		return appOnScreen();
	}

	/**
	 * Видна ли человеку лента этого чата.
	 *
	 * Выбранный чат — ещё не чат на экране: на телефоне поверх него лежит список,
	 * а на компьютере его место занимают «Настройки», «Почта» и «Сервисы». Какая
	 * панель сейчас показана, знает оболочка (ui.svelte.ts), она и подставляет сюда
	 * свою проверку. До этого «прочитано» ставилось по одному выбору чата: человек
	 * смотрел на список, а собеседник уже видел две галочки, и счётчик не появлялся.
	 */
	feedVisible: (chatId: string) => boolean = (chatId) => this.selectedChatId === chatId;

	/** Читает ли человек этот чат прямо сейчас: приложение на экране и лента видна. */
	private reading(chatId: string): boolean {
		return this.onScreen() && this.feedVisible(chatId);
	}

	/** Лента открытого чата снова перед глазами — можно считать его прочитанным. */
	markReadIfVisible(chatId: string): void {
		if (this.reading(chatId)) this.markRead(chatId);
	}

	/** Вернулись к приложению — открытый чат можно считать прочитанным. */
	private handleVisibility = (): void => {
		if (!this.onScreen()) {
			this.flushDrafts();
			return;
		}
		if (this.selectedChatId) this.markReadIfVisible(this.selectedChatId);
	};

	private applyNewMessage(message: Message): void {
		const chat = this.chats.find((item) => item.id === message.chatId);
		if (!chat) {
			/*
			 * Чат неизвестен: обычно это первое письмо от нового собеседника, и
			 * объявление о чате либо ещё в пути, либо потерялось на обрыве.
			 * Раньше сообщение здесь просто выбрасывалось и не появлялось до
			 * перезахода — теперь перечитываем список чатов и применяем заново.
			 */
			void this.recoverUnknownChat(message);
			return;
		}
		const list = this.messagesByChat[message.chatId];
		if (list) {
			const index = list.findIndex((item) => item.id === message.id);
			if (index >= 0) list[index] = message;
			else list.push(message);
			list.sort((a, b) => a.seq - b.seq);
		}

		chat.lastSeq = Math.max(chat.lastSeq, message.seq);
		if (!chat.lastMessage || message.seq >= chat.lastMessage.seq) chat.lastMessage = message;
		// Сообщение пришло — значит, уже не печатает: отметке незачем висеть ещё четыре секунды.
		this.setTyping(message.chatId, message.authorId, false);
		if (message.authorId === this.me.id) {
			// Следующее же нажатие после своей отправки снова сообщит о наборе.
			this.typingSentAt.delete(message.chatId);
			chat.lastReadSeq = Math.max(chat.lastReadSeq, message.seq);
			chat.unreadCount = Math.max(0, chat.lastSeq - chat.lastReadSeq);
		} else if (this.reading(message.chatId)) {
			// Свёрнутое приложение и чат под списком не читают: иначе собеседник
			// видел бы две галочки, пока телефон лежит в кармане, а свой счётчик
			// обнулялся бы на невиденном. Отметка поставится при возвращении.
			this.markRead(message.chatId);
		} else {
			chat.unreadCount = Math.max(0, chat.lastSeq - chat.lastReadSeq);
		}
	}

	/**
	 * Сообщить собеседникам, что печатаем.
	 *
	 * Зовётся на каждое нажатие, а уходит не чаще раза в три секунды: отметка
	 * у собеседника живёт четыре, так что между двумя сообщениями она не гаснет.
	 */
	notifyTyping(chatId: string): void {
		const chat = this.chats.find((item) => item.id === chatId);
		// В канале читателей тысячи, а в «Избранном» и у бота читать это некому.
		if (!chat || chat.kind === 'channel' || chat.isSaved || chat.isBot) return;

		const now = Date.now();
		if (now - (this.typingSentAt.get(chatId) ?? 0) < TYPING_SEND_EVERY_MS) return;
		if (!socket.isOpen) return;

		this.typingSentAt.set(chatId, now);
		socket.send({
			op: 'chat.typing',
			id: crypto.randomUUID(),
			ts: now,
			payload: { chatId }
		});
	}

	/**
	 * Поставить или снять отметку «печатает…».
	 *
	 * Таймер один на человека в чате и с каждым событием заводится заново.
	 * Раньше на каждое событие ставился свой, и первый из них гасил отметку
	 * через четыре секунды после начала набора, сколько бы человек ни печатал.
	 * Чат ищется заново: `chat.updated` подменяет объект, и старая ссылка
	 * правила бы уже не тот, что на экране.
	 */
	private setTyping(chatId: string, userId: string, on: boolean): void {
		const key = `${chatId}:${userId}`;
		const timer = this.typingTimers.get(key);
		if (timer) clearTimeout(timer);
		this.typingTimers.delete(key);

		const chat = this.chats.find((item) => item.id === chatId);
		if (!chat) return;

		const current = chat.typingUserIds ?? [];
		const others = current.filter((id) => id !== userId);
		if (!on) {
			if (others.length !== current.length) chat.typingUserIds = others;
			return;
		}

		if (others.length === current.length) chat.typingUserIds = [...others, userId];
		this.typingTimers.set(
			key,
			setTimeout(() => this.setTyping(chatId, userId, false), TYPING_SHOWN_MS)
		);
	}

	selectChat(chatId: string): void {
		// Черновик текущего чата сохраняем перед уходом, иначе набранный
		// текст пропадёт при переключении — самая заметная потеря данных
		// в мессенджере из возможных.
		this.selectedChatId = chatId;
		this.replyTarget = null;
		this.editTarget = null;
		this.clearMessageSelection();
		this.infoPanelOpen = false;
		// Открытый чат — часть сохранённого вида: со следующим запуском
		// приложение откроется там же, где его закрыли.
		this.scheduleSave();

		// Переписка подгружается при первом открытии чата, а не вся сразу:
		// иначе вход в приложение тянул бы историю всех чатов разом.
		void this.loadMessages(chatId).then(() => this.markReadIfVisible(chatId));
	}

	async openSearchResult(message: Message): Promise<void> {
		/*
		 * Сначала сама переписка — её конец. Раньше чат, в который ещё не
		 * заходили, считался загруженным по одним окрестностям найденного: это
		 * был обрывок, из которого к последним сообщениям не выйти.
		 */
		await this.loadMessages(message.chatId);
		await this.loadAround(message);
		this.messageFocusId = message.id;
		this.selectChat(message.chatId);
	}

	/** Догрузить окрестности сообщения отдельным куском ленты. */
	private async loadAround(message: Message): Promise<void> {
		const context = await fetchMessages(message.chatId, { aroundSeq: message.seq, limit: 100 });
		if (context.length === 0) return;

		// Куски берём до вставки: по пустой ещё ленте их не восстановить.
		const ranges = (this.messagesByChat[message.chatId] ?? []).length
			? this.rangesOf(message.chatId)
			: [];
		this.absorb(message.chatId, context);
		const seqs = context.map((item) => item.seq);
		this.setRanges(message.chatId, [...ranges, { lo: Math.min(...seqs), hi: Math.max(...seqs) }]);
	}

	/**
	 * Показать в открытом чате сообщение, которого в ленте может не быть, —
	 * закреплённое из глубины истории. Чат при этом не переоткрывается: начатый
	 * ответ и выделение остаются на месте.
	 */
	async showInFeed(message: Message): Promise<void> {
		const present = (this.messagesByChat[message.chatId] ?? []).some(
			(item) => item.id === message.id
		);
		if (!present) {
			try {
				await this.loadAround(message);
			} catch {
				toasts.error('Не удалось открыть сообщение');
				return;
			}
		}
		this.messageFocusId = message.id;
	}

	/**
	 * Показать сообщение в переписке и подсветить его.
	 *
	 * От `openSearchResult` отличается тем, что сообщение уже на руках: из
	 * просмотра фотографии известно и оно, и чат. Если переписка не загружена,
	 * догружаем окрестности тем же путём, что и поиск.
	 */
	focusMessage(chatId: string, messageId: string): void {
		const message = (this.messagesByChat[chatId] ?? []).find((item) => item.id === messageId);
		if (!message) {
			this.selectChat(chatId);
			return;
		}

		this.messageFocusId = messageId;
		this.selectChat(chatId);
	}

	clearMessageFocus(messageId: string): void {
		if (this.messageFocusId === messageId) this.messageFocusId = null;
	}

	setSection(section: Section): void {
		this.section = section;
		this.searchQuery = '';
		if (section === 'calls') void this.viewMissedCalls();
	}

	/* --- Профиль -------------------------------------------------------- */

	updateProfile(
		patch: Partial<
			Pick<
				User,
				| 'name'
				| 'lastName'
				| 'bio'
				| 'phone'
				| 'avatarUrl'
				| 'avatarColor'
				| 'emojiStatus'
				| 'profileAccent'
				| 'serviceRole'
			>
		>
	): void {
		Object.assign(this.me, patch);

		// Короткое имя — то, чем подписаны сообщения в группах. Если его
		// не обновить вместе с именем, в ленте останется старое.
		this.me.shortName = this.me.name.split(/\s+/)[0] ?? this.me.name;
	}

	/**
	 * Отметка подписки у себя.
	 *
	 * Приходит не из профиля, а из состояния Premium: срок заканчивается сам,
	 * и профиль об этом не узнаёт. Значок рядом со своим именем должен гаснуть
	 * тогда же, когда пропадает квота, — источник у них один.
	 */
	setPremium(active: boolean): void {
		this.me.isPremium = active;
	}

	/**
	 * Рассылка от ANDRESOT. Важная — карточкой поверх экрана, пока её не закроют;
	 * обычная уже лежит сообщением в чате «ANDRESOT», тост нужен только старым
	 * рассылкам без чата.
	 */
	showAnnouncement(announcement: ServiceAnnouncement): void {
		// Личные вести без чата (награда за приглашение и т. п.) — по-прежнему тостом.
		if (!announcement.chatId) {
			toasts.show(`${announcement.title}: ${announcement.body}`);
			return;
		}
		if (!announcement.mandatory) return;
		if (!this.announcements.some((item) => item.id === announcement.id)) {
			this.announcements = [...this.announcements, announcement];
		}
	}

	dismissAnnouncement(id: string): void {
		this.announcements = this.announcements.filter((item) => item.id !== id);
	}

	/** Перечитать серверный список после создания личного диалога и открыть его. */
	async openServerChat(chatId: string): Promise<void> {
		const lifecycle = this.lifecycle;
		const [chats, users] = await Promise.all([fetchChats(), fetchUsers()]);
		if (lifecycle !== this.lifecycle) return;
		this.adoptChats(chats);
		this.users = users;
		this.setSection('chats');
		this.selectChat(chatId);
	}

	/** Применить свежий серверный снимок после REST-настроек канала. */
	mergeChat(chat: Chat): void {
		this.applyChatUpdate(chat);
	}

	/** Убрать удалённый канал без ожидания WebSocket-события. */
	forgetChat(chatId: string): void {
		this.removeChatLocally(chatId);
	}

	/* --- Роли и меры в чате ---------------------------------------------- */

	/**
	 * Назначить или снять роль. Хранится исключениями: обычный участник
	 * из карты удаляется, а не записывается со значением `member`.
	 */
	async setChatRole(chatId: string, userId: string, role: ChatRole): Promise<void> {
		// Владелец назначается не сменой роли, а передачей прав: там владелец
		// одновременно перестаёт быть владельцем, и одним полем это не выразить.
		if (role === 'owner') throw new Error('Владелец назначается передачей прав');
		this.applyChatUpdate(await chatManagementApi.updateMember(chatId, userId, { role }));
	}

	/** Ограничить участника на час или снять ограничение. */
	async toggleRestriction(chatId: string, userId: string): Promise<void> {
		const chat = this.chats.find((c) => c.id === chatId);
		if (!chat) return;
		const active = (chat.restrictions?.[userId] ?? 0) > Date.now();
		this.applyChatUpdate(
			await chatManagementApi.updateMember(chatId, userId, {
				restrictedForMinutes: active ? null : 60
			})
		);
	}

	async removeMember(chatId: string, userId: string): Promise<void> {
		this.applyChatUpdate(await chatManagementApi.removeMember(chatId, userId, false));
	}

	/** Исключить и запретить возвращаться по ссылке. */
	async banMember(chatId: string, userId: string): Promise<void> {
		this.applyChatUpdate(await chatManagementApi.removeMember(chatId, userId, true));
	}

	async unbanMember(chatId: string, userId: string): Promise<void> {
		this.applyChatUpdate(await chatManagementApi.unbanMember(chatId, userId));
	}

	async setMemberPermissions(chatId: string, patch: Partial<MemberPermissions>): Promise<void> {
		this.applyChatUpdate(await chatManagementApi.updatePermissions(chatId, patch));
	}

	/** Правка группы: название, описание, аватар, реакции. */
	async updateChat(chatId: string, patch: ChatUpdate): Promise<void> {
		this.applyChatUpdate(await chatManagementApi.updateChat(chatId, patch));
	}

	/**
	 * Передать права владельца. С выходом чат пропадает из списка: смотреть
	 * его больше не с чего, и ждать серверного события незачем.
	 */
	async transferChatOwner(
		chatId: string,
		targetUserId: string,
		leaveAfterTransfer: boolean
	): Promise<void> {
		const chat = await chatManagementApi.transferOwner(chatId, {
			targetUserId,
			leaveAfterTransfer
		});
		if (chat) this.applyChatUpdate(chat);
		else this.forgetChat(chatId);
	}

	async addChatMembers(chatId: string, userIds: string[]): Promise<void> {
		this.applyChatUpdate(await chatManagementApi.addMembers(chatId, userIds));
		await this.refreshUsers();
	}

	async joinChat(token: string): Promise<chatManagementApi.JoinChatResult> {
		const result = await chatManagementApi.joinChat(token);
		if (result.status === 'joined') {
			this.applyChatUpdate(result.chat);
			await this.refreshUsers();
		}
		return result;
	}

	async leaveChat(chatId: string): Promise<void> {
		await chatManagementApi.leaveChat(chatId);
		this.removeChatLocally(chatId);
	}

	/* --- Папки ---------------------------------------------------------- */

	selectFolder(folderId: string): void {
		this.activeFolderId = folderId;
		this.section = 'chats';
		this.searchQuery = '';
	}

	async createFolder(
		title: string,
		rules: FolderRules = {},
		chatIds: string[] = []
	): Promise<string> {
		const folder = await folderApi.createFolder({
			title,
			rules,
			chatIds,
			excludedChatIds: []
		});
		this.folders = [...this.folders, folder];
		return folder.id;
	}

	async updateFolder(
		folderId: string,
		patch: Partial<Omit<ChatFolder, 'id' | 'isBuiltIn'>>
	): Promise<void> {
		const folder = this.folders.find((f) => f.id === folderId);
		if (!folder || folder.isBuiltIn) return;

		const updated = await folderApi.updateFolder(folderId, {
			...(patch.title !== undefined ? { title: patch.title } : {}),
			...(patch.rules !== undefined ? { rules: patch.rules } : {}),
			...(patch.chatIds !== undefined ? { chatIds: patch.chatIds } : {}),
			...(patch.excludedChatIds !== undefined ? { excludedChatIds: patch.excludedChatIds } : {})
		});
		Object.assign(folder, updated);
	}

	async deleteFolder(folderId: string): Promise<void> {
		const folder = this.folders.find((f) => f.id === folderId);
		// Встроенную «Все» удалить нельзя: без неё не останется способа
		// увидеть чаты, не попавшие ни в одну папку.
		if (!folder || folder.isBuiltIn) return;

		await folderApi.deleteFolder(folderId);
		this.folders = this.folders.filter((f) => f.id !== folderId);
		if (this.activeFolderId === folderId) {
			this.activeFolderId = this.folders.find((item) => item.isBuiltIn)?.id ?? '';
		}
	}

	/** Перестановка папки на новое место. Встроенная «Все» с места не двигается. */
	async moveFolder(folderId: string, targetIndex: number): Promise<void> {
		const movable = this.sortedFolders.filter((f) => !f.isBuiltIn);
		const from = movable.findIndex((f) => f.id === folderId);
		if (from === -1) return;

		const reordered = [...movable];
		const [moved] = reordered.splice(from, 1);
		if (!moved) return;

		reordered.splice(Math.max(0, Math.min(targetIndex, reordered.length)), 0, moved);
		this.folders = await folderApi.reorderFolders(reordered.map((folder) => folder.id));
	}

	markRead(chatId: string): void {
		const chat = this.chats.find((c) => c.id === chatId);
		// Ручная отметка «непрочитано» бывает и на дочитанном чате. Раньше такой чат
		// сюда не проходил, и точку нельзя было снять ни открытием, ни из меню.
		if (!chat || (chat.lastReadSeq >= chat.lastSeq && !chat.isUnread)) return;

		chat.unreadCount = 0;
		chat.lastReadSeq = chat.lastSeq;
		chat.isUnread = false;

		// Сообщаем серверу, иначе на другом устройстве чат останется
		// непрочитанным, хотя человек его уже видел.
		socket.request('chat.read', { chatId, seq: chat.lastSeq }).catch(() => undefined);
	}

	draftFor(chatId: string): string {
		return this.drafts[chatId] ?? '';
	}

	setDraft(chatId: string, text: string): void {
		if (text) this.drafts[chatId] = text;
		else delete this.drafts[chatId];

		const chat = this.chats.find((c) => c.id === chatId);
		if (chat) chat.draft = text.trim() ? text : undefined;
		if (this.draftSaveTimer) clearTimeout(this.draftSaveTimer);
		if (!text) void this.flushDrafts();
		else this.draftSaveTimer = setTimeout(() => void this.flushDrafts(), 200);
	}

	private async flushDrafts(): Promise<void> {
		if (this.draftSaveTimer) clearTimeout(this.draftSaveTimer);
		this.draftSaveTimer = null;
		if (!this.me.id || !this.draftStorageAvailable) return;
		const userId = this.me.id;
		const saved = await writeDrafts(userId, $state.snapshot(this.drafts));
		if (!saved && this.me.id === userId) this.draftStorageAvailable = false;
	}

	async persistForDesktopRestart(): Promise<boolean> {
		if (!this.me.id) return true;
		await this.flushDrafts();
		if (!this.draftStorageAvailable && Object.keys(this.drafts).length > 0) return false;
		if (this.saveTimer) clearTimeout(this.saveTimer);
		this.saveTimer = null;
		return this.saveNow();
	}

	/**
	 * Отправка сообщения.
	 *
	 * Сообщение появляется в ленте сразу со статусом «отправляется» и лишь
	 * потом получает подтверждение WebSocket-шлюза.
	 */
	/**
	 * Отправить сообщение.
	 *
	 * Хвост — объектом, а не вереницей позиционных аргументов: с шестым по
	 * счёту вызов вида `undefined, undefined, storyId` однажды попадает не
	 * в тот разъём, и понять это по месту вызова нельзя.
	 */
	async sendMessage(
		chatId: string,
		text: string,
		attachments: Attachment[] = [],
		options: {
			/** Визитка: сообщение становится карточкой человека. */
			contactUserId?: string;
			/** Через сколько исчезнет приложенное фото или видео. */
			ephemeral?: EphemeralMedia;
			/** История, на которую отвечают. */
			storyId?: string;
		} = {}
	): Promise<boolean> {
		const id = crypto.randomUUID();
		const message = this.enqueue(chatId, id, text, attachments, options);
		if (!message) return false;
		// Once the optimistic message is durable, its text can leave the editor.
		const savedLocally = await this.saveNow();
		if (savedLocally && this.draftFor(chatId) === text) {
			this.setDraft(chatId, '');
			await this.flushDrafts();
		}

		if (socket.isOpen) {
			void this.deliver(chatId, id, text.trim(), {
				replyToId: message.replyTo?.id,
				attachmentIds: attachments.map((a) => a.id),
				contactUserId: options.contactUserId,
				ephemeral: options.ephemeral,
				storyId: options.storyId
			}).then((delivered) => {
				if (delivered && this.draftFor(chatId) === text) this.setDraft(chatId, '');
			});
		}
		return true;
	}

	/**
	 * Положить своё сообщение в ленту, не дожидаясь сервера.
	 *
	 * Общая часть обычной отправки и отправки фотографий: у второй между
	 * появлением сообщения и уходом на сервер проходит вся загрузка, но в
	 * переписке оно обязано выглядеть точно так же.
	 */
	private enqueue(
		chatId: string,
		id: string,
		text: string,
		attachments: Attachment[],
		options: { contactUserId?: string; ephemeral?: EphemeralMedia; storyId?: string } = {}
	): Message | null {
		const { contactUserId, ephemeral } = options;
		const trimmed = text.trim();

		// Пустой текст допустим, если есть вложение или визитка: фотографию
		// часто отправляют без подписи.
		if (!trimmed && attachments.length === 0 && !contactUserId) return null;

		const chat = this.chats.find((c) => c.id === chatId);
		if (!chat) return null;

		const replyTo = this.replyTarget;
		const first = attachments[0];

		const message: Message = {
			id,
			chatId,
			// Предполагаемый номер. Настоящий назначает сервер — он придёт
			// с подтверждением и заменит этот.
			seq: chat.lastSeq + 1,
			authorId: this.me.id,
			// Вид определяется вложением: изображение показывается картинкой,
			// файл — карточкой со скачиванием.
			kind: contactUserId ? 'contact' : first ? first.kind : 'text',
			text: trimmed || undefined,
			contactUserId,
			attachments: attachments.length > 0 ? attachments : undefined,
			// Свой же пузырь сразу закрыт: автор не открывает исчезающее фото,
			// и показывать ему картинку до ответа сервера было бы обманом.
			ephemeral: ephemeral ? { mode: ephemeral, burned: false } : undefined,
			createdAt: Date.now(),
			delivery: this.linkDown() ? 'queued' : 'sending',
			replyTo: replyTo
				? { id: replyTo.id, authorId: replyTo.authorId, text: replyTo.text ?? 'Вложение' }
				: undefined
		};

		// Сообщение появляется на экране сразу, не дожидаясь сервера: ожидание
		// ответа перед показом делает переписку заметно медленнее на вид.
		this.messagesByChat[chatId] = [...(this.messagesByChat[chatId] ?? []), message];
		chat.lastMessage = message;
		this.scheduleSave();

		this.replyTarget = null;
		return message;
	}

	/**
	 * Связи нет — по соединению или по словам браузера.
	 *
	 * Одного соединения мало: оборвавшийся канал браузер закрывает не сразу и всё
	 * это время числит его открытым. О пропавшей сети `navigator.onLine` узнаёт
	 * раньше, а когда она вернётся, соединение пересоберётся и очередь уйдёт сама.
	 */
	private linkDown(): boolean {
		return !socket.isOpen || (typeof navigator !== 'undefined' && !navigator.onLine);
	}

	/**
	 * Доля готовности вложений, которые ещё грузятся: сообщение → вложение → 0…1.
	 *
	 * Живёт отдельно от самого сообщения, а не полем в нём: договор с сервером
	 * про долю загрузки ничего не знает, и класть в него временное значило бы
	 * тащить его и в сохранение на устройство, и в обмен по сети.
	 */
	uploading = $state<Record<string, Record<string, number>>>({});

	/** Файлы ещё не ушедших сообщений — по ним работает повтор, пока открыта страница. */
	private mediaDrafts = new Map<string, { chatId: string; text: string; files: File[] }>();

	/** Ждёт ли сообщение загрузки своих вложений. */
	isUploading(messageId: string): boolean {
		return this.uploading[messageId] !== undefined;
	}

	/**
	 * Отправить фотографии так, как это делают в мессенджерах.
	 *
	 * Сообщение появляется в переписке сразу — со снимками из выбранных файлов
	 * и кольцом готовности поверх каждого; загрузка идёт уже после. Раньше было
	 * наоборот: фотографии ждали в поле ввода, пока не загрузятся, а в
	 * переписке всё это время было пусто — и человек не понимал, отправилось
	 * что-нибудь или нет.
	 */
	async sendMedia(
		chatId: string,
		text: string,
		files: File[],
		options: { ephemeral?: EphemeralMedia } = {}
	): Promise<boolean> {
		if (files.length === 0) return false;

		const id = crypto.randomUUID();
		const local: Attachment[] = files.map((file, position) => ({
			// Свой номер: настоящий даст сервер, когда файл доедет.
			id: `local-${id}-${position}`,
			kind: file.type.startsWith('video/')
				? ('video' as const)
				: file.type.startsWith('image/')
					? ('image' as const)
					: ('file' as const),
			url: URL.createObjectURL(file),
			previewUrl: URL.createObjectURL(file),
			name: file.name,
			size: file.size
		}));

		if (!this.enqueue(chatId, id, text, local, options)) {
			for (const item of local) this.releaseLocal(item);
			return false;
		}
		const savedLocally = await this.saveNow();
		if (savedLocally && this.draftFor(chatId) === text) {
			this.setDraft(chatId, '');
			await this.flushDrafts();
		}

		this.uploading[id] = Object.fromEntries(local.map((item) => [item.id, 0]));
		this.mediaDrafts.set(id, { chatId, text: text.trim(), files });
		/*
		 * Загрузку не ждём: поле ввода должно очиститься сразу, как только
		 * сообщение появилось в переписке. Иначе снимки видны уже и там, и в
		 * поле, а набранная подпись висит под ними до конца передачи.
		 */
		void this.uploadAndDeliver(chatId, id, text.trim(), files, local, options);
		return true;
	}

	/** Загрузить файлы и, когда все доехали, отправить сообщение. */
	private async uploadAndDeliver(
		chatId: string,
		id: string,
		text: string,
		files: File[],
		local: Attachment[],
		options: { ephemeral?: EphemeralMedia }
	): Promise<boolean> {
		try {
			const attachments = await Promise.all(
				files.map(
					(file, position) =>
						uploadFile(file, (progress) => {
							const row = this.uploading[id];
							const key = local[position]?.id;
							if (row && key) row[key] = progress.ratio;
						}).promise
				)
			);

			const message = this.messagesByChat[chatId]?.find((item) => item.id === id);
			// Сообщение могли удалить, пока шла загрузка, — тогда отправлять нечего.
			if (!message) return false;
			message.attachments = attachments;

			delete this.uploading[id];
			this.mediaDrafts.delete(id);
			/*
			 * Ссылки на местные файлы намеренно не отпускаем.
			 *
			 * На них может смотреть уже открытый просмотр: снимок открывают, не
			 * дожидаясь конца загрузки, и отпущенная ссылка превращала бы
			 * открытую фотографию в пустоту — а «Сохранить» и «Поделиться»
			 * переставали работать. Браузер освободит их сам при перезагрузке.
			 */

			const delivered = await this.deliver(chatId, id, text, {
				// Ответ с фотографией — тоже ответ: без этого цитата была видна, пока шла
				// загрузка, и пропадала с подтверждением; собеседник её не получал вовсе.
				replyToId: message.replyTo?.id,
				attachmentIds: attachments.map((item) => item.id),
				ephemeral: options.ephemeral
			});
			if (delivered && this.draftFor(chatId) === text) this.setDraft(chatId, '');
			return delivered;
		} catch (error) {
			delete this.uploading[id];
			/*
			 * Нет связи — это не отказ: сообщение ждёт сети, как и текстовое, и уйдёт
			 * само, когда она вернётся (flushOutbox повторит загрузку). Раньше снимок,
			 * отправленный в лифте, оставался «не отправлено», пока его не ткнут.
			 */
			const dropped =
				error instanceof NetworkError || (error instanceof ApiError && error.status === 0);
			// Очередь разбирается при восстановлении связи, поэтому ждать в ней можно
			// только тогда, когда связь и правда оборвана: иначе повторять было бы некому.
			if (dropped && this.linkDown() && this.mediaDrafts.has(id)) {
				this.updateDelivery(chatId, id, 'queued');
				return false;
			}
			/*
			 * Не доехало — помечаем «не отправлено» и оставляем снимки на месте:
			 * человек должен видеть, что именно не ушло, и повторить.
			 */
			this.updateDelivery(chatId, id, 'failed');
			toasts.error(error instanceof Error ? error.message : 'Не удалось отправить фотографии');
			return false;
		}
	}

	/** Отпустить местный файл — для снимков, которые так и не ушли в переписку. */
	private releaseLocal(attachment: Attachment): void {
		// Ссылки `blob:` держат файл в памяти, пока их не отпустят.
		if (attachment.url.startsWith('blob:')) URL.revokeObjectURL(attachment.url);
		if (attachment.previewUrl?.startsWith('blob:')) URL.revokeObjectURL(attachment.previewUrl);
	}

	/** Отправить на сервер и отметить исход в ленте. */
	private async deliver(
		chatId: string,
		id: string,
		text: string,
		options: {
			replyToId?: string;
			attachmentIds?: string[];
			contactUserId?: string;
			ephemeral?: EphemeralMedia;
			storyId?: string;
		} = {}
	): Promise<boolean> {
		try {
			const ack = await socket.request('message.send', { chatId, text, ...options }, id);

			if (ack.op === 'ack' && ack.payload.message) {
				const saved = ack.payload.message;
				const list = this.messagesByChat[chatId];
				const index = list?.findIndex((m) => m.id === id) ?? -1;

				/*
				 * Заменяем на то, что записал сервер: у него настоящий номер
				 * и время. Свою догадку о номере отбрасываем.
				 *
				 * `delivery` намеренно остаётся пустым: сообщение ушло, и
				 * дальше галочку выводит $utils/delivery по номеру прочтения.
				 */
				if (list && index >= 0) {
					list[index] = saved;
				}

				const chat = this.chats.find((c) => c.id === chatId);
				if (chat) {
					/*
					 * Только вперёд. Пока шло подтверждение, в чат могло прийти
					 * чужое сообщение с бо́льшим номером: простое присваивание
					 * откатывало превью списка на своё, уводило номер последнего
					 * назад и оставляло счётчик непрочитанного застрявшим.
					 */
					chat.lastSeq = Math.max(chat.lastSeq, saved.seq);
					chat.lastReadSeq = Math.max(chat.lastReadSeq, saved.seq);
					if (!chat.lastMessage || saved.seq >= chat.lastMessage.seq) chat.lastMessage = saved;
					chat.unreadCount = Math.max(0, chat.lastSeq - chat.lastReadSeq);
				}

				this.scheduleSave();
				return true;
			}

			this.updateDelivery(chatId, id, 'failed');
			return false;
		} catch (problem) {
			// Явный отказ сервера требует решения человека. Обрыв транспорта,
			// напротив, остаётся в очереди и уйдёт при следующем reconnect.
			const refused = problem instanceof SocketRequestError;
			this.updateDelivery(chatId, id, refused ? 'failed' : 'queued');
			// Причину отказа человек должен увидеть: красная пометка без объяснения
			// оставляла его нажимать «Повторить» на том, что не уйдёт никогда.
			if (refused) toasts.error(problem.message);
			return false;
		}
	}

	private updateDelivery(chatId: string, messageId: string, state: Message['delivery']): void {
		const list = this.messagesByChat[chatId];
		if (!list) return;

		const message = list.find((m) => m.id === messageId);
		if (message) message.delivery = state;

		// Своё неотправленное тоже переживает перезагрузку: иначе написанное
		// без связи исчезало бы вместе с вкладкой.
		this.scheduleSave();
	}

	/** Повторная отправка сообщения, которое не ушло. */
	retryMessage(chatId: string, messageId: string): void {
		void this.resend(chatId, messageId);
	}

	/**
	 * Повторить отправку одного сообщения.
	 *
	 * Тот же идентификатор — если первая попытка всё-таки дошла, сервер вернёт
	 * уже записанное сообщение, а не создаст второе.
	 */
	private async resend(chatId: string, messageId: string): Promise<boolean> {
		const message = this.messagesByChat[chatId]?.find((m) => m.id === messageId);
		if (!message) return false;

		/*
		 * Фотографии, которые так и не загрузились, повторяются с загрузки:
		 * серверных номеров у них ещё нет, и отправлять было бы нечего.
		 */
		const draft = this.mediaDrafts.get(messageId);
		if (draft) {
			const local = message.attachments ?? [];
			this.uploading[messageId] = Object.fromEntries(local.map((item) => [item.id, 0]));
			this.updateDelivery(chatId, messageId, 'sending');
			return this.uploadAndDeliver(chatId, messageId, draft.text, draft.files, local, {
				...(message.ephemeral?.mode ? { ephemeral: message.ephemeral.mode } : {})
			});
		}

		/*
		 * Файлы живут, пока открыта страница. После перезапуска от сообщения остаётся
		 * только запись со ссылками на них: загружать нечего, а отправлять её как есть
		 * нельзя — сервер таких вложений не знает. Его можно только удалить.
		 */
		if (message.attachments?.some((item) => item.id.startsWith('local-'))) {
			this.updateDelivery(chatId, messageId, 'failed');
			return false;
		}

		this.updateDelivery(chatId, messageId, 'sending');

		return this.deliver(chatId, messageId, message.text ?? '', {
			replyToId: message.replyTo?.id,
			attachmentIds: message.attachments?.map((a) => a.id),
			// Визитка без этого поля — пустое сообщение: сервер отказывал, и повтор
			// не срабатывал никогда.
			contactUserId: message.contactUserId,
			ephemeral: message.ephemeral?.mode,
			storyId: message.storyReply?.storyId
		});
	}

	/**
	 * Отправить всё, что не ушло, — связь вернулась.
	 *
	 * Отдельной очереди нет: неотправленное и так лежит в переписке с пометкой,
	 * и второй список тех же сообщений однажды разошёлся бы с первым. Порядок —
	 * по времени написания: иначе на том конце разговор перемешается.
	 *
	 * Раньше не дошедшее ждало пальца: написал три сообщения в лифте — три раза
	 * ткни в каждое, когда выедешь.
	 */
	private async flushOutbox(): Promise<void> {
		const waiting: Array<{ chatId: string; message: Message }> = [];
		for (const [chatId, list] of Object.entries(this.messagesByChat)) {
			for (const message of list) {
				if (message.authorId !== this.me.id) continue;
				// Незагруженные фотографии пропускаем: у их вложений пока свои
				// номера, и сервер такое сообщение просто отвергнет.
				if (this.isUploading(message.id)) continue;
				if (message.delivery === 'queued' || message.delivery === 'sending') {
					waiting.push({ chatId, message });
				}
			}
		}

		waiting.sort((a, b) => a.message.createdAt - b.message.createdAt);

		for (const item of waiting) {
			// Связь снова пропала — остальное уйдёт со следующим возвращением.
			if (!socket.isOpen) return;
			await this.resend(item.chatId, item.message.id);
		}
	}

	/**
	 * Открыть исчезающее фото. С этого мгновения идёт отсчёт.
	 *
	 * Ответ сервера обязателен: до него у вложения нет доступа, и показывать
	 * картинку раньше было бы показом пустоты.
	 */
	async revealMessage(messageId: string): Promise<Message | null> {
		const ack = await socket.request('message.reveal', { messageId });
		if (ack.op !== 'ack' || !ack.payload.message) return null;
		this.applyUpdatedMessage(ack.payload.message);
		return ack.payload.message;
	}

	/** Закрыть просмотр. Сервер сотрёт файл, не дожидаясь уборщика. */
	async burnMessage(messageId: string): Promise<void> {
		const ack = await socket.request('message.burn', { messageId });
		if (ack.op === 'ack' && ack.payload.message) this.applyUpdatedMessage(ack.payload.message);
	}

	async editMessage(messageId: string, text: string): Promise<void> {
		const message = await editChatMessage(messageId, text);
		this.applyUpdatedMessage(message);
		if (this.editTarget?.id === messageId) this.editTarget = null;
	}

	async deleteMessage(chatId: string, messageId: string): Promise<void> {
		/*
		 * Своё неотправленное сервер может и не знать: отказ или обрыв случились до
		 * записи. Раньше «Удалить» на таком сообщении всегда шло на сервер, получало
		 * «нельзя удалить», и красный пузырь оставался в переписке навсегда.
		 */
		const local = this.messagesByChat[chatId]?.find((item) => item.id === messageId);
		const unsent =
			local?.authorId === this.me.id &&
			(local.delivery === 'failed' || local.delivery === 'queued');
		if (unsent && !socket.isOpen) {
			this.discardUnsent(chatId, messageId);
			return;
		}

		// Сначала ждём серверную проверку прав. Иначе чужое сообщение исчезало
		// локально даже после отказа и возвращалось только после перезагрузки.
		try {
			const ack = await socket.request('message.delete', { messageId });
			if (ack.op !== 'ack' || !ack.payload.message) {
				throw new Error('Сервер не вернул состояние удалённого сообщения');
			}
			this.applyUpdatedMessage(ack.payload.message);
		} catch (problem) {
			// Сервер такого сообщения не знает — значит, оно есть только здесь.
			if (!unsent) throw problem;
			this.discardUnsent(chatId, messageId);
		}
	}

	/** Убрать у себя сообщение, которое так и не ушло на сервер. */
	private discardUnsent(chatId: string, messageId: string): void {
		const list = this.messagesByChat[chatId];
		const message = list?.find((item) => item.id === messageId);
		if (!list || !message) return;

		for (const attachment of message.attachments ?? []) this.releaseLocal(attachment);
		delete this.uploading[messageId];
		this.mediaDrafts.delete(messageId);
		this.messagesByChat[chatId] = list.filter((item) => item.id !== messageId);

		const chat = this.chats.find((item) => item.id === chatId);
		if (chat?.lastMessage?.id === messageId) {
			chat.lastMessage = this.messagesByChat[chatId]?.at(-1);
		}
		this.scheduleSave();
	}

	async toggleReaction(messageId: string, emoji: ReactMessagePayload['emoji']): Promise<void> {
		const ack = await socket.request('message.react', { messageId, emoji });
		if (ack.op !== 'ack' || !ack.payload.message) {
			throw new Error('Сервер не вернул реакцию');
		}
		this.applyUpdatedMessage(ack.payload.message);
	}

	async setMessagePinned(messageId: string, pinned: boolean): Promise<void> {
		const ack = await socket.request('message.pin', { messageId, pinned });
		if (ack.op !== 'ack' || !ack.payload.message) {
			throw new Error('Сервер не вернул закрепление');
		}
		this.applyUpdatedMessage(ack.payload.message);
	}

	/**
	 * Отправить визитку человека в выбранные чаты.
	 *
	 * Отправка идёт по одной: у каждого сообщения свой идентификатор, и при
	 * обрыве связи повторится ровно то, что не дошло, а не вся рассылка.
	 */
	async shareContact(userId: string, targetChatIds: string[]): Promise<void> {
		for (const chatId of targetChatIds) {
			await this.sendMessage(chatId, '', [], { contactUserId: userId });
		}
		toasts.success(
			targetChatIds.length === 1 ? 'Контакт отправлен' : `Отправлено в ${targetChatIds.length} чата`
		);
	}

	async forwardMessages(messageIds: string[], targetChatIds: string[]): Promise<void> {
		const ack = await socket.request('message.forward', { messageIds, targetChatIds });
		if (ack.op !== 'ack' || !ack.payload.messages) {
			throw new Error('Сервер не вернул пересланные сообщения');
		}
		for (const message of ack.payload.messages) this.applyNewMessage(message);
		this.clearMessageSelection();
	}

	async createPoll(chatId: string, poll: Omit<CreatePollPayload, 'chatId'>): Promise<void> {
		const ack = await socket.request('poll.create', { chatId, ...poll });
		if (ack.op !== 'ack' || !ack.payload.message) {
			throw new Error('Сервер не вернул созданный опрос');
		}
		this.applyNewMessage(ack.payload.message);
	}

	async votePoll(pollId: string, optionIds: string[]): Promise<void> {
		const ack = await socket.request('poll.vote', { pollId, optionIds });
		if (ack.op !== 'ack' || !ack.payload.message) {
			throw new Error('Сервер не вернул результат голосования');
		}
		this.applyUpdatedMessage(ack.payload.message);
	}

	async closePoll(pollId: string): Promise<void> {
		const ack = await socket.request('poll.close', { pollId });
		if (ack.op !== 'ack' || !ack.payload.message) {
			throw new Error('Сервер не вернул состояние опроса');
		}
		this.applyUpdatedMessage(ack.payload.message);
	}

	toggleMessageSelection(message: Message): void {
		if (this.messageSelectionChatId && this.messageSelectionChatId !== message.chatId) {
			this.clearMessageSelection();
		}
		this.messageSelectionChatId = message.chatId;
		this.selectedMessageIds = this.selectedMessageIds.includes(message.id)
			? this.selectedMessageIds.filter((id) => id !== message.id)
			: [...this.selectedMessageIds, message.id];
		if (this.selectedMessageIds.length === 0) this.messageSelectionChatId = null;
	}

	isMessageSelected(messageId: string): boolean {
		return this.selectedMessageIds.includes(messageId);
	}

	clearMessageSelection(): void {
		this.selectedMessageIds = [];
		this.messageSelectionChatId = null;
	}

	setReplyTarget(message: Message | null): void {
		this.replyTarget = message;
		if (message) this.editTarget = null;
	}

	setEditTarget(message: Message | null): void {
		this.editTarget = message;
		if (message) this.replyTarget = null;
	}

	private applyUpdatedMessage(message: Message): void {
		const list = this.messagesByChat[message.chatId];
		if (list) {
			const index = list.findIndex((item) => item.id === message.id);
			if (index >= 0) list[index] = message;
		}

		const chat = this.chats.find((item) => item.id === message.chatId);
		if (chat?.lastMessage?.id === message.id) chat.lastMessage = message;

		const pinned = this.pinnedMessagesByChat[message.chatId];
		if (pinned) {
			const without = pinned.filter((item) => item.id !== message.id);
			this.pinnedMessagesByChat[message.chatId] = message.isPinned
				? [message, ...without].sort((a, b) => (b.pinnedAt ?? 0) - (a.pinnedAt ?? 0))
				: without;
		}
	}

	async togglePin(chatId: string): Promise<boolean> {
		const chat = this.chats.find((c) => c.id === chatId);
		if (!chat) return false;
		const preferences = await updateChatPreferences(chatId, { isPinned: !chat.isPinned });
		chat.isPinned = preferences.isPinned;
		chat.isMuted = preferences.isMuted;
		return chat.isPinned;
	}

	async toggleMute(chatId: string): Promise<boolean> {
		const chat = this.chats.find((c) => c.id === chatId);
		if (!chat) return false;
		return this.setMute(chatId, !chat.isMuted, null);
	}

	/**
	 * Выключить звук на срок или вернуть его.
	 *
	 * @param until Момент, до которого молчать; `null` — навсегда, то есть
	 * пока не включат обратно. При включении звука срок обнуляется сам.
	 */
	async setMute(chatId: string, muted: boolean, until: number | null): Promise<boolean> {
		const chat = this.chats.find((c) => c.id === chatId);
		if (!chat) return false;
		const preferences = await updateChatPreferences(chatId, {
			isMuted: muted,
			mutedUntil: muted ? until : null
		});
		chat.isPinned = preferences.isPinned;
		chat.isMuted = preferences.isMuted;
		chat.mutedUntil = preferences.mutedUntil ?? undefined;
		return chat.isMuted;
	}

	/**
	 * Отметить непрочитанным вручную. Открытый чат перед этим закрывается:
	 * иначе отметка снимется тут же — просмотр сразу отправит «прочитано».
	 */
	async markUnread(chatId: string): Promise<void> {
		const chat = this.chats.find((c) => c.id === chatId);
		if (!chat) return;
		if (this.selectedChatId === chatId) this.selectedChatId = null;
		const preferences = await updateChatPreferences(chatId, { isUnread: true });
		chat.isUnread = preferences.isUnread;
	}

	/**
	 * Отметить непрочитанным начиная с выбранного сообщения — из меню самого
	 * сообщения. Чат при этом закрывается: иначе просмотр тут же отправит
	 * «прочитано» и отметка снимется.
	 */
	async markUnreadFrom(chatId: string, seq: number): Promise<void> {
		const chat = this.chats.find((c) => c.id === chatId);
		if (!chat) return;
		if (this.selectedChatId === chatId) this.selectedChatId = null;

		const preferences = await updateChatPreferences(chatId, { isUnread: true, unreadFromSeq: seq });
		chat.isUnread = preferences.isUnread;
		chat.lastReadSeq = seq - 1;
		chat.unreadCount = Math.max(0, chat.lastSeq - chat.lastReadSeq);
	}

	/** Стереть переписку у себя. Сообщения собеседника это не трогает. */
	async clearHistory(chatId: string): Promise<void> {
		await clearChatHistory(chatId);

		const chat = this.chats.find((c) => c.id === chatId);
		if (chat) {
			chat.lastMessage = undefined;
			chat.lastReadSeq = chat.lastSeq;
			chat.unreadCount = 0;
			chat.isUnread = false;
		}
		// Загруженное надо забыть целиком, иначе стёртая переписка останется
		// на экране до перезагрузки страницы.
		delete this.messagesByChat[chatId];
		delete this.pinnedMessagesByChat[chatId];
		this.loaded.delete(chatId);
		this.loadedPinned.delete(chatId);
	}

	/**
	 * Убрать чат из списка. В группе и канале это выход, в диалоге — скрытие:
	 * из диалога выйти нельзя, иначе собеседник не смог бы больше написать.
	 */
	async removeChat(chatId: string): Promise<void> {
		const chat = this.chats.find((c) => c.id === chatId);
		if (!chat) return;

		if (chat.kind === 'group' || chat.kind === 'channel') {
			await this.leaveChat(chatId);
			return;
		}

		await deleteChat(chatId);
		this.removeChatLocally(chatId);
	}

	/* --- Звонки --------------------------------------------------------- */

	private async viewMissedCalls(): Promise<void> {
		try {
			const result = await callApi.markCallsViewed();
			for (const call of this.calls) {
				if (
					!call.isOutgoing &&
					call.outcome === 'missed' &&
					call.endedAt !== undefined &&
					call.endedAt <= result.viewedThrough
				) {
					call.viewedAt = result.viewedThrough;
				}
			}
		} catch (error) {
			toasts.error(
				error instanceof Error ? error.message : 'Не удалось отметить звонки просмотренными'
			);
		}
	}

	private callRecord(call: ServerCall): CallRecord {
		const chat = this.chats.find((item) => item.id === call.chatId);
		return {
			id: call.id,
			chatId: call.chatId,
			peerId:
				chat?.peerId ??
				call.participants.find((participant) => participant.userId !== this.me.id)?.userId ??
				this.me.id,
			kind: call.kind,
			outcome: call.outcome,
			duration: call.duration,
			isOutgoing: call.initiatorId === this.me.id,
			startedAt: call.startedAt,
			endedAt: call.endedAt,
			viewedAt: call.viewedAt,
			participantIds: call.participants.map((participant) => participant.userId),
			recordings: call.recordings
		};
	}

	private upsertCallRecord(call: ServerCall): void {
		this.trackOngoing(call);
		if (call.phase !== 'ended') return;
		const record = this.callRecord(call);
		const index = this.calls.findIndex((item) => item.id === call.id);
		if (index >= 0) this.calls[index] = record;
		else this.calls = [record, ...this.calls];
	}

	/** Запомнить идущий звонок чата или забыть закончившийся. */
	private trackOngoing(call: ServerCall): void {
		const known = this.ongoingCalls[call.chatId];
		if (call.phase === 'ended') {
			if (known?.id === call.id) delete this.ongoingCalls[call.chatId];
			return;
		}
		// Тот же звонок из запоздавшего ответа не должен затереть более свежий снимок.
		if (known?.id === call.id && known.revision > call.revision) return;
		this.ongoingCalls[call.chatId] = call;
	}

	/**
	 * Идущий групповой звонок чата, в который можно войти отсюда.
	 *
	 * Не тот, что уже открыт на этом экране, и не тот, где мы уже в комнате с
	 * другого устройства: второй вход под тем же именем выбил бы первый.
	 */
	joinableCall(chatId: string): ServerCall | null {
		const call = this.ongoingCalls[chatId];
		if (!call || this.activeCall?.id === call.id) return null;
		const chat = this.chats.find((item) => item.id === chatId);
		if (!chat || chat.kind === 'dialog') return null;
		const me = call.participants.find((participant) => participant.userId === this.me.id);
		if (me?.state === 'joined') return null;
		return call;
	}

	/** Войти в идущий звонок группы из её чата. */
	async joinChatCall(chatId: string): Promise<void> {
		const known = this.joinableCall(chatId);
		if (!known) return;
		if (this.activeCall) {
			toasts.show('Сначала завершите текущий звонок');
			return;
		}

		// Отметка до запроса: событие о входе приходит раньше ответа, и без неё
		// эта вкладка сочла бы звонок чужим — принятым где-то ещё.
		const claimed = !this.callsHere.has(known.id);
		this.claimCall(known.id);
		this.locallyEndingCallIds.delete(known.id);
		try {
			const call = await callApi.joinCall(known.id);
			this.upsertCallRecord(call);
			this.acceptCallRevision(call);
			this.activeCall = this.activeFromServer(call, 'incoming');
			this.callMinimized = false;
			const me = call.participants.find((participant) => participant.userId === this.me.id);
			// Зал ожидания: дальше — когда впустит организатор (см. обработку `call.state`).
			if (me?.state !== 'invited') await this.acceptCall();
		} catch (error) {
			if (claimed) this.releaseCall(known.id);
			if (this.activeCall?.id === known.id) this.dismissCall(known.id);
			toasts.error(error instanceof Error ? error.message : 'Не удалось войти в звонок');
			// Чаще всего звонок просто закончился, а событие об этом не дошло.
			void this.resyncCalls();
		}
	}

	private applyDeletedCallHistory(
		payload: { scope: 'all' } | { scope: 'selected'; callIds: string[] }
	): void {
		if (payload.scope === 'all') {
			this.calls = [];
			return;
		}
		const hidden = new Set(payload.callIds);
		this.calls = this.calls.filter((call) => !hidden.has(call.id));
	}

	async deleteCallHistory(callIds?: string[]): Promise<number> {
		const input = callIds ? ({ scope: 'selected', callIds } as const) : ({ scope: 'all' } as const);
		const result = await callApi.deleteCallHistory(input);
		this.applyDeletedCallHistory(input);
		return result.deleted;
	}

	private claimCall(callId: string): void {
		if (this.callsHere.has(callId)) return;
		this.callsHere.add(callId);
		writeCallsHere(this.callsHere);
	}

	private releaseCall(callId: string): void {
		if (!this.callsHere.delete(callId)) return;
		writeCallsHere(this.callsHere);
	}

	/**
	 * Касается ли звонок этой вкладки.
	 *
	 * Свой — всегда. Чужой для неё (начат или принят на другом устройстве того же
	 * человека) — только пока он звонит и на него ещё никто не ответил: звонить
	 * должны все устройства, а разговаривать — одно.
	 */
	private callConcernsThisTab(call: ServerCall): boolean {
		if (this.callsHere.has(call.id)) return true;
		const me = call.participants.find((participant) => participant.userId === this.me.id);
		return me?.state === 'ringing' && call.initiatorId !== this.me.id && !me.acceptedAt;
	}

	/** Убрать экран звонка, который для этой вкладки закончился. */
	private dismissCall(callId: string, interrupted = false): void {
		if (interrupted) callRingtone.interrupted(callId);
		else callRingtone.stop(callId);
		if (this.activeCall?.id !== callId) return;
		this.activeCall = null;
		this.callMinimized = false;
		this.preparedCall = null;
		void callMedia.disconnect();
	}

	/**
	 * Принять список звонков с сервера — при запуске и после обрыва связи.
	 *
	 * После обрыва он нужен не меньше, чем при запуске: «звонок завершён» до
	 * отключённого устройства не доходит. Экран разговора оставался висеть, а
	 * входящий звонил, пока его не сбросят рукой.
	 */
	private adoptCallSnapshot(snapshot: {
		available: boolean;
		recordingAvailable: boolean;
		calls: ServerCall[];
	}): void {
		this.callsAvailable = snapshot.available;
		this.callsRecordingAvailable = snapshot.recordingAvailable;
		this.calls = snapshot.calls
			.filter((call) => call.phase === 'ended')
			.map((call) => this.callRecord(call));
		this.ongoingCalls = Object.fromEntries(
			snapshot.calls.filter((call) => call.phase !== 'ended').map((call) => [call.chatId, call])
		);

		// Отметки о своих звонках, которые уже закончились, больше не нужны.
		const open = new Set(
			snapshot.calls.filter((call) => call.phase !== 'ended').map((call) => call.id)
		);
		for (const id of [...this.callsHere]) {
			// Свой текущий звонок не трогаем: список мог быть собран до его начала.
			if (open.has(id) || id === this.activeCall?.id) continue;
			this.releaseCall(id);
		}

		const mine = (call: ServerCall) =>
			call.participants.find((participant) => participant.userId === this.me.id);

		const active = this.activeCall;
		if (active) {
			const current = snapshot.calls.find((call) => call.id === active.id);
			// Звонка в списке нет — судить не о чем: оставляем как есть.
			if (!current) return;
			const me = mine(current);
			const over =
				current.phase === 'ended' || !me || me.state === 'left' || me.state === 'declined';
			if (over || !this.callConcernsThisTab(current)) {
				this.callRevisions.set(current.id, current.revision);
				this.dismissCall(
					active.id,
					over && (active.phase === 'active' || active.phase === 'reconnecting')
				);
				if (over) void this.refreshChat(current.chatId).catch(() => undefined);
			} else if (active.phase !== 'prejoin' && this.acceptCallRevision(current)) {
				this.activeCall = this.activeFromServer(current, active.phase);
			}
			return;
		}

		const current = snapshot.calls.find((call) => {
			if (call.phase === 'ended' || this.locallyEndingCallIds.has(call.id)) return false;
			const me = mine(call);
			if (!me || me.state === 'declined' || me.state === 'left') return false;
			return this.callConcernsThisTab(call);
		});
		if (!current) return;

		this.activeCall = this.activeFromServer(current);
		this.callMinimized = false;
		// Вкладку перезагрузили посреди своего разговора — возвращаемся в него.
		if (this.callsHere.has(current.id) && mine(current)?.state === 'joined') {
			void this.connectMedia(current);
		}
	}

	/** Сверить звонки с сервером: пока связи не было, могло случиться что угодно. */
	private async resyncCalls(): Promise<void> {
		const lifecycle = this.lifecycle;
		try {
			const snapshot = await callApi.fetchCalls();
			if (lifecycle === this.lifecycle) this.adoptCallSnapshot(snapshot);
		} catch {
			// Следующая сверка повторит.
		}
	}

	/** Добрать то, что не доехало при запуске: папки и контакты. */
	private async completeStart(): Promise<void> {
		const lifecycle = this.lifecycle;
		const [folders, contacts] = await Promise.all([
			folderApi.fetchFolders().catch(() => null),
			fetchContacts().catch(() => null)
		]);
		if (lifecycle !== this.lifecycle) return;

		if (folders) {
			this.folders = folders;
			if (!folders.some((folder) => folder.id === this.activeFolderId)) {
				this.activeFolderId =
					folders.find((folder) => folder.isBuiltIn)?.id ?? folders[0]?.id ?? '';
			}
		}
		if (contacts) this.setContacts(contacts);
		// Людей и звонки сверка перечитывает сама — здесь остаются только эти двое.
		this.startIncomplete = !folders || !contacts;
		this.scheduleSave();
	}

	private activeFromServer(call: ServerCall, previousPhase?: ActiveCall['phase']): ActiveCall {
		const knownRevision = this.callRevisions.get(call.id);
		if (
			knownRevision !== undefined &&
			call.revision < knownRevision &&
			this.activeCall?.id === call.id
		) {
			return this.activeCall;
		}
		/*
		 * Два транспорта могут принести одну revision в разном порядке. После
		 * ACTIVE равный по номеру снимок времён ringing не имеет права вернуть
		 * экран к «Соединение…»; завершение при этом всегда принимается.
		 */
		if (
			knownRevision !== undefined &&
			call.revision <= knownRevision &&
			this.activeCall?.id === call.id &&
			(this.activeCall.phase === 'active' || this.activeCall.phase === 'reconnecting') &&
			call.phase !== 'active' &&
			call.phase !== 'ended'
		) {
			return this.activeCall;
		}
		this.callRevisions.set(call.id, Math.max(knownRevision ?? -1, call.revision));
		const me = call.participants.find((participant) => participant.userId === this.me.id);
		let phase: ActiveCall['phase'];
		if (call.phase === 'ended') phase = 'ended';
		else if (
			me?.state === 'ringing' &&
			call.initiatorId !== this.me.id &&
			!me.acceptedAt &&
			previousPhase !== 'connecting'
		)
			phase = 'incoming';
		else if (call.phase === 'active')
			phase = previousPhase === 'reconnecting' ? 'reconnecting' : 'active';
		else phase = call.initiatorId === this.me.id ? 'outgoing' : 'connecting';

		return {
			id: call.id,
			chatId: call.chatId,
			kind: call.kind,
			phase,
			isOutgoing: call.initiatorId === this.me.id,
			connectedAt: call.acceptedAt,
			recording: call.recording,
			recordings: call.recordings,
			isGroup: (this.chats.find((chat) => chat.id === call.chatId)?.kind ?? 'dialog') !== 'dialog',
			participants: call.participants.map((participant) => ({
				userId: participant.userId,
				role: participant.role,
				state: participant.state,
				isMuted: participant.isMuted,
				isCameraOn: participant.isCameraOn,
				isScreenSharing: participant.isScreenSharing,
				isSpeaking: false,
				handRaised: participant.handRaised,
				connectionQuality:
					participant.state === 'joined' ? callMedia.connectionQualityFor(participant.userId) : 0
			}))
		};
	}

	private acceptCallRevision(call: ServerCall): boolean {
		const knownRevision = this.callRevisions.get(call.id);
		if (knownRevision !== undefined && call.revision < knownRevision) return false;
		this.callRevisions.set(call.id, call.revision);
		return true;
	}

	private async connectMedia(call: ServerCall, initial?: InitialCallMediaOptions): Promise<void> {
		const existing = this.mediaConnections.get(call.id);
		if (existing) return existing;

		const pending = this.establishMedia(call, initial).finally(() => {
			if (this.mediaConnections.get(call.id) === pending) this.mediaConnections.delete(call.id);
		});
		this.mediaConnections.set(call.id, pending);
		return pending;
	}

	private async establishMedia(call: ServerCall, initial?: InitialCallMediaOptions): Promise<void> {
		try {
			const credentials = await callApi.fetchCallCredentials(call.id);
			if (this.activeCall?.id !== call.id) return;
			await callMedia.connect(credentials, call.kind, initial);
			if (this.activeCall?.id !== call.id) {
				if (callMedia.connectedCallId === call.id) await callMedia.disconnect();
				return;
			}

			const me = this.activeCall.participants.find(
				(participant) => participant.userId === this.me.id
			);
			const isMuted = !callMedia.microphoneEnabled;
			const isCameraOn = callMedia.cameraEnabled;
			if (me) {
				me.isMuted = isMuted;
				me.isCameraOn = isCameraOn;
			}
			const updated = await callApi.updateCallState(call.id, {
				isMuted,
				isCameraOn
			});
			if (this.activeCall?.id === call.id) {
				/*
				 * Пока запускались камера и микрофон, signed webhook LiveKit мог
				 * уже перевести звонок в ACTIVE. Исходный `call` — снимок до этих
				 * ожиданий; чтение его фазы здесь возвращало работающий разговор в
				 * «Соединение…». Ответ state содержит свежую revision, а общий merge
				 * не принимает снимок старее уже полученного по WebSocket.
				 */
				this.activeCall = this.activeFromServer(updated, this.activeCall.phase);
			}
		} catch (error) {
			callMedia.error = error instanceof Error ? error.message : 'Не удалось подключить звонок';
		}
	}

	/** Восстановить конкретный входящий вызов после Web Push или deep link. */
	async openIncomingCall(callId: string, connectAccepted = false): Promise<boolean> {
		const snapshot = await callApi.fetchCalls();
		this.callsAvailable = snapshot.available;
		this.callsRecordingAvailable = snapshot.recordingAvailable;
		for (const item of snapshot.calls) this.upsertCallRecord(item);
		const call = snapshot.calls.find((item) => item.id === callId);
		const participant = call?.participants.find((item) => item.userId === this.me.id);
		if (
			!call ||
			call.phase === 'ended' ||
			!participant ||
			participant.state === 'declined' ||
			participant.state === 'left'
		) {
			if (this.activeCall?.id === callId) this.activeCall = null;
			return false;
		}

		if (this.activeCall && this.activeCall.id !== callId) return false;
		// «Принять» в системном уведомлении — ответ с этого устройства.
		const acceptHere =
			connectAccepted && Boolean(participant.acceptedAt) && participant.state === 'ringing';
		if (acceptHere) this.claimCall(callId);
		if (!this.callConcernsThisTab(call)) {
			// Уже ответили с другого устройства: открывать здесь нечего.
			if (this.activeCall?.id === callId) this.dismissCall(callId);
			return false;
		}
		this.locallyEndingCallIds.delete(callId);
		/*
		 * «Соединение…» — только когда ответ действительно принят здесь и сейчас
		 * начнётся подключение. Раньше хватало одной пометки в адресе: если сервер
		 * ответа не подтвердил, экран оставался на «Соединение…» навсегда — без
		 * подключения и без кнопки «Принять».
		 */
		this.activeCall = this.activeFromServer(call, acceptHere ? 'connecting' : undefined);
		this.callMinimized = false;
		if (acceptHere) await this.acceptCall();
		return true;
	}

	async startCall(chatId: string, kind: CallKind): Promise<void> {
		const chat = this.chats.find((c) => c.id === chatId);
		if (!chat) return;
		/*
		 * Кнопка, которая ничего не делает, — худший ответ: человек жмёт её снова и
		 * снова. Раньше так и было, если идёт другой звонок или список звонков при
		 * запуске не доехал и «звонки доступны» осталось невыставленным.
		 */
		if (this.activeCall) {
			toasts.show('Сначала завершите текущий звонок');
			return;
		}
		if (!this.callsAvailable) {
			await this.resyncCalls();
			if (!this.callsAvailable) {
				toasts.error('Звонки сейчас недоступны');
				return;
			}
			// Пока перечитывали, мог прийти входящий — второй звонок поверх не начинаем.
			if (this.activeCall) return;
		}
		try {
			const result = await callApi.createCall({ chatId, kind });
			this.claimCall(result.call.id);
			this.upsertCallRecord(result.call);
			/*
			 * В группе перед входом показываем себя: там звонок не «набирает
			 * номер», а идёт своим ходом, и попасть в него с включённой камерой
			 * врасплох — обычное дело. В личном звонке ждать нечего: собеседник
			 * один, и гудки начинаются сразу.
			 */
			const group = chat.kind !== 'dialog';
			// Фазу назначаем поверх: `activeFromServer` выводит её из состояния
			// звонка на сервере и о подготовке ничего не знает — она наша.
			const active = this.activeFromServer(result.call, 'outgoing');
			this.activeCall = group ? { ...active, phase: 'prejoin' } : active;
			this.callMinimized = false;
			// Полное описание звонка нужно, чтобы подключиться после экрана
			// подготовки: в списке звонков лежит только запись о нём.
			this.preparedCall = group ? result.call : null;
			if (!group) await this.connectMedia(result.call);
		} catch (error) {
			toasts.error(error instanceof Error ? error.message : 'Не удалось начать звонок');
		}
	}

	/**
	 * Войти в звонок с экрана подготовки. Медиа подключается только здесь —
	 * до нажатия человек виден лишь себе.
	 */
	async enterPreparedCall(initial: InitialCallMediaOptions): Promise<void> {
		const active = this.activeCall;
		const call = this.preparedCall;
		if (!active || active.phase !== 'prejoin' || !call) return;
		this.activeCall = { ...active, phase: 'connecting' };
		this.preparedCall = null;
		await this.connectMedia(call, initial);
	}

	/** Уйти с экрана подготовки, не входя: звонок на сервере закрывается. */
	async cancelPreparedCall(): Promise<void> {
		const active = this.activeCall;
		if (!active || active.phase !== 'prejoin') return;
		const call = this.preparedCall;
		this.activeCall = null;
		this.preparedCall = null;
		this.releaseCall(active.id);
		if (call) await this.cancelCreatedCall(call);
	}

	/**
	 * Групповой звонок «сначала ссылка»: звонок и приглашение к нему создаются
	 * заранее, а медиа подключается отдельно, когда человек нажмёт «Начать».
	 *
	 * Под звонок заводится настоящая группа: своего адреса у звонка на сервере
	 * нет, он всегда живёт в чате. Группа остаётся и после — перепиской тех,
	 * кто созвонился, а не исчезает вместе со звонком.
	 *
	 * Раньше чат для такого звонка создавался только в браузере, с придуманным
	 * идентификатором. Сервер о нём не знал, и звонок не начинался вовсе —
	 * пункт меню был виден, нажимался и не делал ничего.
	 */
	async createGroupCall(
		title: string,
		memberIds: string[]
	): Promise<{ call: ServerCall; link: string } | null> {
		if (!this.callsAvailable) {
			toasts.error('Звонки сейчас недоступны');
			return null;
		}

		const chat = await createManagedChat({ kind: 'group', title, memberIds });
		await this.openServerChat(chat.id);

		const result = await callApi.createCall({ chatId: chat.id, kind: 'video' });
		this.upsertCallRecord(result.call);
		return { call: result.call, link: await callApi.createCallLink(result.call.id) };
	}

	/** Имя по умолчанию для группы под звонок — как в списке чатов. */
	defaultCallTitle(): string {
		const day = new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'long' }).format(
			new Date()
		);
		return `Групповой звонок ${day}`;
	}

	/**
	 * Отменить заранее созданный звонок, в который так и не вошли.
	 *
	 * Без этого закрытый экран оставлял бы на сервере звонок, где никого нет,
	 * а ссылка вела бы в пустоту.
	 */
	async cancelCreatedCall(call: ServerCall): Promise<void> {
		try {
			this.upsertCallRecord(await callApi.endCall(call.id));
		} catch {
			// Звонок мог завершиться и сам. Человек уже закрыл экран, и
			// сообщение об этом ему нечем помочь.
		}
	}

	/** Войти в заранее созданный звонок — только здесь подключается медиа. */
	async enterCreatedCall(call: ServerCall): Promise<void> {
		if (this.activeCall) return;
		this.locallyEndingCallIds.delete(call.id);
		this.claimCall(call.id);
		this.activeCall = this.activeFromServer(call, 'outgoing');
		this.callMinimized = false;
		await this.connectMedia(call);
	}

	async joinCallByLink(token: string): Promise<void> {
		const call = await callApi.joinCallLink(token);
		this.locallyEndingCallIds.delete(call.id);
		this.claimCall(call.id);
		this.upsertCallRecord(call);
		this.activeCall = this.activeFromServer(call, 'incoming');
		this.callMinimized = false;
		const me = call.participants.find((participant) => participant.userId === this.me.id);
		if (me?.state !== 'invited') await this.acceptCall();
	}

	async manageCallParticipant(
		userId: string,
		input: { role?: 'speaker' | 'listener'; admit?: boolean }
	): Promise<void> {
		if (!this.activeCall) return;
		try {
			const call = await callApi.manageCallParticipant(this.activeCall.id, userId, input);
			this.activeCall = this.activeFromServer(call, this.activeCall.phase);
		} catch (error) {
			toasts.error(error instanceof Error ? error.message : 'Не удалось изменить участника');
		}
	}

	/** Принять подтверждённое сервером приглашение и подключить медиатранспорт. */
	async acceptCall(): Promise<void> {
		if (!this.activeCall) return;
		const callId = this.activeCall.id;
		// Отметка ставится до запроса: событие «принят» придёт раньше ответа, и без
		// неё эта же вкладка сочла бы, что ответили где-то ещё.
		const claimed = !this.callsHere.has(callId);
		this.claimCall(callId);
		this.activeCall.phase = 'connecting';
		try {
			const call = await callApi.acceptCall(callId);
			this.upsertCallRecord(call);
			if (this.activeCall?.id !== callId) return;
			this.activeCall = this.activeFromServer(call, 'connecting');
			await this.connectMedia(call);
		} catch (error) {
			if (claimed) this.releaseCall(callId);
			if (this.activeCall?.id === callId) this.activeCall.phase = 'incoming';
			toasts.error(error instanceof Error ? error.message : 'Не удалось принять звонок');
		}
	}

	async endCall(): Promise<void> {
		const active = this.activeCall;
		if (!active) return;
		this.locallyEndingCallIds.add(active.id);
		callRingtone.cancel(active.id);
		this.activeCall = null;
		this.preparedCall = null;
		this.callMinimized = false;
		this.releaseCall(active.id);
		await callMedia.disconnect();
		try {
			const call =
				active.phase === 'incoming'
					? await callApi.declineCall(active.id)
					: await callApi.endCall(active.id);
			this.upsertCallRecord(call);
			void this.refreshChat(active.chatId).catch(() => undefined);
		} catch (error) {
			this.locallyEndingCallIds.delete(active.id);
			toasts.error(
				error instanceof Error ? error.message : 'Сервер не подтвердил завершение звонка'
			);
		}
	}

	async toggleMicrophone(): Promise<void> {
		const active = this.activeCall;
		if (!active) return;
		const callId = active.id;
		if (callMedia.connectedCallId !== callId || !callMedia.ready) return;
		const initial = active.participants.find((participant) => participant.userId === this.me.id);
		if (!initial) return;
		// Запоминаем намерение до ожидания media setup: за это время начальное
		// включение устройства может само изменить серверный снимок.
		const muted =
			callMedia.connectedCallId === callId && callMedia.ready
				? callMedia.microphoneEnabled
				: !initial.isMuted;
		const me =
			this.activeCall?.id === callId
				? this.activeCall.participants.find((participant) => participant.userId === this.me.id)
				: null;
		if (me) await this.setMicrophone(muted);
	}

	async toggleCamera(): Promise<void> {
		const active = this.activeCall;
		if (!active) return;
		const callId = active.id;
		if (callMedia.connectedCallId !== callId || !callMedia.ready) return;
		const initial = active.participants.find((participant) => participant.userId === this.me.id);
		if (!initial) return;
		const enabled =
			callMedia.connectedCallId === callId && callMedia.ready
				? !callMedia.cameraEnabled
				: !initial.isCameraOn;
		const me = this.activeCall?.participants.find((p) => p.userId === this.me.id);
		if (!me || this.activeCall?.id !== callId) return;
		try {
			me.isCameraOn = await callMedia.setCamera(enabled);
			await callApi.updateCallState(callId, { isCameraOn: me.isCameraOn });
		} catch {
			// `callMedia` уже сохранил точную причину именно для камеры.
		}
	}

	async toggleScreenShare(): Promise<void> {
		const active = this.activeCall;
		if (!active) return;
		const callId = active.id;
		if (callMedia.connectedCallId !== callId || !callMedia.ready) return;
		const initial = active.participants.find((participant) => participant.userId === this.me.id);
		if (!initial) return;
		const enabled =
			callMedia.connectedCallId === callId && callMedia.ready
				? !callMedia.screenShareEnabled
				: !initial.isScreenSharing;
		const me = this.activeCall?.participants.find((p) => p.userId === this.me.id);
		if (!me || this.activeCall?.id !== callId) return;
		callMedia.error = null;
		try {
			me.isScreenSharing = await callMedia.setScreenShare(enabled);
			await callApi.updateCallState(callId, { isScreenSharing: me.isScreenSharing });
		} catch (error) {
			const name = error instanceof Error ? error.name : '';
			if (name === 'NotAllowedError' || name === 'AbortError') {
				toasts.show('Демонстрация экрана не начата');
				return;
			}
			callMedia.error = screenShareError(error);
		}
	}

	async toggleHand(): Promise<void> {
		const me = this.activeCall?.participants.find((p) => p.userId === this.me.id);
		if (!me || !this.activeCall) return;
		const previous = me.handRaised;
		me.handRaised = !previous;
		try {
			await callApi.updateCallState(this.activeCall.id, { handRaised: me.handRaised });
		} catch (error) {
			me.handRaised = previous;
			toasts.error(error instanceof Error ? error.message : 'Не удалось изменить состояние руки');
		}
	}

	/** Включить или выключить микрофон принудительно — для режима рации. */
	async setMicrophone(muted: boolean): Promise<void> {
		const callId = this.activeCall?.id;
		if (!callId) return;
		if (callMedia.connectedCallId !== callId || !callMedia.ready) return;
		const me = this.activeCall?.participants.find((p) => p.userId === this.me.id);
		if (!me || this.activeCall?.id !== callId) return;
		try {
			me.isMuted = !(await callMedia.setMicrophone(!muted));
			await callApi.updateCallState(callId, { isMuted: me.isMuted });
		} catch {
			// `callMedia` уже сохранил точную причину именно для микрофона.
		}
	}

	/** Переключить боковую панель; повторное нажатие той же закрывает её. */
	toggleCallPanel(panel: Exclude<CallSidePanel, 'none'>): void {
		this.callSidePanel = this.callSidePanel === panel ? 'none' : panel;
	}

	setCallViewMode(mode: CallViewMode): void {
		this.callViewMode = mode;

		// В режиме «один крупно» кто-то должен быть выбран: если никого
		// не выбирали, берём первого собеседника.
		if (mode === 'speaker' && !this.callFocusedId) {
			this.callFocusedId =
				this.activeCall?.participants.find((p) => p.userId !== this.me.id)?.userId ?? null;
		}
	}

	/** Перебор участников стрелками влево и вправо. */
	focusNeighbour(delta: 1 | -1): void {
		const others = this.activeCall?.participants.filter((p) => p.userId !== this.me.id) ?? [];
		if (others.length === 0) return;

		const current = others.findIndex((p) => p.userId === this.callFocusedId);
		// Перебор по кругу: с последнего снова на первого.
		const next = (current + delta + others.length) % others.length;

		this.callFocusedId = others[next]?.userId ?? null;
		this.callViewMode = 'speaker';
	}

	/** Ссылка-приглашение на текущий звонок. */
	async callLink(): Promise<string> {
		return this.activeCall ? callApi.createCallLink(this.activeCall.id) : '';
	}

	async startCallRecording(): Promise<void> {
		if (!this.activeCall || !this.callsRecordingAvailable) return;
		try {
			const call = await callApi.startCallRecording(this.activeCall.id);
			this.activeCall = this.activeFromServer(call, this.activeCall.phase);
		} catch (error) {
			toasts.error(error instanceof Error ? error.message : 'Не удалось включить запись');
		}
	}

	async stopCallRecording(): Promise<void> {
		if (!this.activeCall) return;
		try {
			const call = await callApi.stopCallRecording(this.activeCall.id);
			this.recordingReadyFor.delete(call.id);
			this.activeCall = this.activeFromServer(call, this.activeCall.phase);
			await callMedia.setRecordingMode(false);
		} catch (error) {
			toasts.error(error instanceof Error ? error.message : 'Не удалось остановить запись');
		}
	}

	async syncCallRecordingMode(recording: boolean): Promise<void> {
		const call = this.activeCall;
		if (!call || callMedia.connectedCallId !== call.id) return;
		try {
			await callMedia.setRecordingMode(recording);
			if (!recording) {
				this.recordingReadyFor.delete(call.id);
				return;
			}
			if (this.recordingReadyFor.has(call.id)) return;
			this.recordingReadyFor.add(call.id);
			const updated = await callApi.confirmCallRecordingReady(call.id);
			if (this.activeCall?.id === call.id) {
				this.activeCall = this.activeFromServer(updated, this.activeCall.phase);
			}
		} catch (error) {
			this.recordingReadyFor.delete(call.id);
			toasts.error(error instanceof Error ? error.message : 'Не удалось подготовить запись');
		}
	}

	async downloadCallRecording(url: string, id: string): Promise<void> {
		try {
			await callApi.downloadCallRecording(url, `andresot-call-${id}.mp4`);
		} catch (error) {
			toasts.error(error instanceof Error ? error.message : 'Не удалось скачать запись');
		}
	}

	/* --- Справочное ------------------------------------------------------ */

	userById(id: string): User {
		if (id === this.me.id) return this.me;

		return (
			this.users.find((u) => u.id === id) ?? {
				id,
				login: 'unknown',
				name: 'Неизвестный',
				presence: 'offline'
			}
		);
	}

	/** Собеседник в диалоге — для показа присутствия в шапке. */
	peerOf(chat: Chat): User | null {
		return chat.peerId ? this.userById(chat.peerId) : null;
	}
}

/**
 * Подходит ли чат под папку.
 *
 * Порядок проверок важен: ручное исключение сильнее любого правила,
 * а ручное добавление сильнее того, что чат под правила не подходит.
 * Иначе нельзя выкинуть из папки «Работа» один надоевший групповой чат.
 */
function chatMatchesFolder(
	chat: Chat,
	folder: ChatFolder | null,
	contactIds: ReadonlySet<string>
): boolean {
	if (!folder) return true;
	if (folder.isBuiltIn) return true;

	if (folder.excludedChatIds.includes(chat.id)) return false;
	if (folder.chatIds.includes(chat.id)) return true;

	const { contacts, nonContacts, groups, channels, unread, sound } = folder.rules;

	// Отметка «непрочитано» вручную считается наравне со счётчиком: человек
	// поставил её именно затем, чтобы чат остался на виду.
	if (unread && chat.unreadCount === 0 && !chat.isUnread) return false;
	if (sound && chat.isMuted) return false;

	// Если правила по типам не заданы вообще, тип чата не ограничивает —
	// иначе папка «Непрочитанные» оказалась бы всегда пустой.
	const hasKindRule = Boolean(contacts || nonContacts || groups || channels);
	if (!hasKindRule) return Boolean(unread || sound);

	if (chat.kind === 'group') return Boolean(groups);
	if (chat.kind === 'channel') return Boolean(channels);

	// Остаётся личная переписка. Собеседник без записи в контактах —
	// «не контакт»; сюда же попадают диалоги, где собеседник неизвестен.
	return chat.peerId && contactIds.has(chat.peerId) ? Boolean(contacts) : Boolean(nonContacts);
}

export const app = new AppState();
